import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MdNote } from 'react-icons/md';
import {
  BsPauseBtnFill,
  BsFillPlayBtnFill,
  BsFillStopBtnFill,
} from 'react-icons/bs';
import CustomMusicModal from '../components/page-components/Dashboard/CustomMusicModal';
import { FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import { TbAlertSquareRoundedFilled } from 'react-icons/tb';
import { RiDeleteBin4Fill, RiMentalHealthLine } from 'react-icons/ri';
import {
  FaEye,
  FaWalking,
  FaTint,
  FaSpa,
  FaHandPaper,
  FaChild,
  FaWind,
} from 'react-icons/fa';
import { MdAccessibilityNew } from 'react-icons/md';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'react-toastify';
import moment from 'moment';
import api from '../api';
import { ProjectSelectorHandle } from '../components/page-components/Dashboard/ProjectSelector';
import { AnalyticsType } from '../components/page-components/Dashboard/types';

import {
  SoloModeContent,
  TeamModeContent,
  RecentSessions,
  FocusAnalytics,
  AllSessionsModal,
  PreSessionModal,
  EndSessionModal,
  NoteViewModal,
  EditTaskModal,
  EditNoteModal,
  FocusSessionType,
  FocusMusicType,
  FocusSessionStatus,
  SessionData,
} from '../components/page-components/Dashboard';
import FocusSessionModal from '../components/page-components/Dashboard/FocusSessionModal';
import SessionCompletionModal from '../components/page-components/Dashboard/SessionCompletionModal';
import SessionDetailsModal from '../components/page-components/Dashboard/SessionDetailsModal';
import GamesSection from '../components/page-components/Dashboard/GamesSection';
import ProjectManagementModal from '../components/page-components/Dashboard/ProjectManagementModal';
import ResumeSessionModal from '../components/page-components/Dashboard/ResumeSessionModal';
import AlertModal from '../components/AlertModal';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { Task as ApiTask } from '../components/page-components/Dashboard/types';
import {
  fetchAllFocusSessions,
  fetchFocusSessionById,
  createFocusSession,
  updateFocusSession,
  setCurrentSession,
} from '../store/slices/focusSessionSlice';
import { fetchGameStats, fetchGameAnalytics } from '../store/slices/gamesSlice';
import { fetchFocusStats } from '../store/slices/analyticsSlice';
import { setSelectedProject } from '../store/slices/projectsSlice';
import useScrollLock from '../hooks/useScrollLock';
import { usePiP } from '../hooks/usePiP';

import { storage } from '../utils/helpers';
import { useNavigate } from 'react-router-dom';

interface DashboardProps {
  currentUser?: {
    id: string;
    name: string;
    email: string;
  };
}

const Dashboard: React.FC<DashboardProps> = ({ currentUser }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    sessions,
    isLoading: sessionsLoading,
    error: sessionsError,
    currentSession,
  } = useAppSelector((state) => state.focusSession);
  const { projects } = useAppSelector((state) => state.projects);

  const {
    pipWindow,
    requestPiP,
    closePiP,
    isSupported: isPiPSupported,
    checkAutoPiPPermission,
  } = usePiP();

  const [greetingMessage, setGreetingMessage] = useState('');
  const [showFeedbackBanner, setShowFeedbackBanner] = useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(false);

  useEffect(() => {
    // Show banner after a short delay to simulate "after dashboard loaded"
    const timer = setTimeout(() => {
      setShowFeedbackBanner(true);
      // Small delay to ensure creating the DOM element before transitioning
      requestAnimationFrame(() => {
        setIsBannerVisible(true);
      });
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const handleCloseBanner = () => {
    setIsBannerVisible(false);
    setTimeout(() => {
      setShowFeedbackBanner(false);
    }, 500);
  };

  const [activeMode, setActiveMode] = useState<'solo' | 'team'>('solo');
  const [teamTab, setTeamTab] = useState<'create' | 'join'>('create');
  const [sessionData, setSessionData] = useState<SessionData>({
    goal: '',
    duration: 25,
    customDuration: '',
    breakReminders: true,
    music: 'silence',
    customMusicURL: undefined,
    roomName: '',
    roomCode: '',
    projectId: undefined,
    tasks: [],
  });
  const [showProjectManagementModal, setShowProjectManagementModal] =
    useState(false);
  const [projectModalView, setProjectModalView] = useState<'list' | 'create'>(
    'list',
  );

  // Room System State
  const [currentRoom, setCurrentRoom] = useState<{
    id: string;
    name: string;
    code: string;
    host: string;
    members: Array<{
      id: string;
      name: string;
      avatar: string;
      isOnline: boolean;
      isMuted: boolean;
    }>;
    settings: {
      duration: number;
      breakReminders: boolean;
      privacy: 'public' | 'private';
      capacity: number;
    };
    status: 'waiting' | 'active' | 'ended';
    createdAt: Date;
  } | null>(null);
  const [isHost, setIsHost] = useState(false);
  const [roomPrivacy, setRoomPrivacy] = useState<'public' | 'private'>(
    'private',
  );
  const [roomCapacity, setRoomCapacity] = useState(10);
  const [availableRooms, setAvailableRooms] = useState<
    Array<{
      id: string;
      name: string;
      host: string;
      memberCount: number;
      capacity: number;
      status: string;
    }>
  >([]);
  const [roomCode, setRoomCode] = useState('');
  const [expandedSessions, setExpandedSessions] = useState<
    Record<string, boolean>
  >({});
  const [showCustomMusicModal, setShowCustomMusicModal] = useState(false);
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [sessionFilter, setSessionFilter] = useState<'all' | 'solo' | 'team'>(
    'all',
  );
  const [sessionSearch, setSessionSearch] = useState('');
  const [userBadges, setUserBadges] = useState<
    Array<{ id: string; name: string; emoji: string; description: string }>
  >([
    {
      id: 'newcomer',
      name: 'Newcomer',
      emoji: '👋',
      description: 'Welcome to Focuzon!',
    },
  ]);

  // Get game stats from Redux store
  const { stats: gameStats } = useAppSelector((state) => state.games);

  // Focus Session State
  const [showFocusSession, setShowFocusSession] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [sessionPaused, setSessionPaused] = useState(false);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(0);
  const [sessionCompleted, setSessionCompleted] = useState(false);
  const [sessionNotes, setSessionNotes] = useState<string[]>([]);
  const [endWarningShown, setEndWarningShown] = useState(false);
  const [showEndWarningMessage, setShowEndWarningMessage] = useState(false);

  // Resume Session Modal State
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [missedSessionTime, setMissedSessionTime] = useState(0);
  const [gapDetectionTime, setGapDetectionTime] = useState<number | null>(null);
  const [beepSoundEnabled, setBeepSoundEnabled] = useState(true);
  const [beepWarningTime, setBeepWarningTime] = useState(59); // seconds before end
  const [previousSessionData, setPreviousSessionData] =
    useState<SessionData | null>(null);
  const [notes, setNotes] = useState<
    Array<{
      id: string;
      text: string;
      createdAt: Date;
    }>
  >([]);
  const [newNote, setNewNote] = useState('');

  // Local UI Task Interface
  interface UITask {
    id: string; // Helper ID for UI
    text?: string;
    title?: string;
    description?: string;
    status?: 'todo' | 'inprogress' | 'done';
    completed?: boolean;
    createdAt: Date | string;
    created_by?: { id: string; name: string } | string;
    assigned_to?: { id: string; name: string } | string | null;
    assignedTo?: { id: string; name: string; avatar: string } | null;
    is_public?: boolean;
  }

  const [tasks, setTasks] = useState<UITask[]>([]);
  const [editingTask, setEditingTask] = useState<UITask | null>(null);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [newTask, setNewTask] = useState('');
  const [reminderAlerts, setReminderAlerts] = useState<
    Array<{
      id: string;
      text: string;
      time: string;
    }>
  >([]);
  const [showNoteModal, setShowNoteModal] = useState(false);

  const [selectedNote, setSelectedNote] = useState<{
    id: string;
    text: string;
    createdAt: Date;
  } | null>(null);

  const [editingNote, setEditingNote] = useState<{
    id: string;
    text: string;
    createdAt: Date;
  } | null>(null);
  const [showEditNoteModal, setShowEditNoteModal] = useState(false);

  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completedSessionDuration, setCompletedSessionDuration] = useState(25);
  const [showSessionDetailsModal, setShowSessionDetailsModal] = useState(false);
  const [selectedSessionForDetails, setSelectedSessionForDetails] = useState<{
    id: string;
    type: 'solo' | 'team';
    goal: string;
    duration: number;
    completedAt: string;
    notes: string;
    members: string[];
    xp: number;
    music?: string;
    customMusicURL?: string;
  } | null>(null);

  // Handler to open session details modal
  const handleViewSessionDetails = (session: {
    id: string;
    type: 'solo' | 'team';
    goal: string;
    duration: number;
    completedAt: string;
    notes: string;
    members: string[];
    xp: number;
    music?: string;
  }) => {
    // Find the full session data to get music and customMusicURL if they exist
    const fullSession = sessions.find((s) => s.id === session.id);
    setSelectedSessionForDetails({
      ...session,
      music: session.music || fullSession?.music,
      customMusicURL: fullSession?.customMusicURL,
    });
    setShowSessionDetailsModal(true);
  };

  // Handler to repeat a session
  const handleRepeatSession = (session: {
    id: string;
    type: 'solo' | 'team';
    goal: string;
    duration: number;
    completedAt: string;
    notes: string;
    members: string[];
    xp: number;
    music?: string;
  }) => {
    // Find the full session data to get customMusicURL if it exists
    const fullSession = sessions.find((s) => s.id === session.id);

    // Ensure minimum duration of 1 minute for repeating sessions
    const sessionDuration = session.duration < 1 ? 1 : session.duration;

    // Set session data with the session's details
    setSessionData({
      goal: session.goal,
      duration: sessionDuration,
      customDuration: '', // Use default duration, not custom
      breakReminders: true, // Default value
      music: (session.music || 'silence') as FocusMusicType,
      customMusicURL: fullSession?.customMusicURL, // Include custom music URL if it exists
      roomName: '',
      roomCode: '',
      projectId: fullSession?.projectId,
      tasks: fullSession?.tasks || [],
    });

    // Set active mode based on session type
    if (session.type === 'team') {
      setActiveMode('team');
    } else {
      setActiveMode('solo');
    }

    // Close any open modals
    setShowAllSessions(false);
    setShowSessionDetailsModal(false);

    // Start the session
    startFocusSession();
  };

  // Team Mode State
  const [teamMembers, setTeamMembers] = useState([
    { id: '1', name: 'John Doe', avatar: 'JD', isOnline: true, isMuted: false },
    {
      id: '2',
      name: 'Sarah Miller',
      avatar: 'SM',
      isOnline: true,
      isMuted: false,
    },
    { id: '3', name: 'Alex Lee', avatar: 'AL', isOnline: true, isMuted: false },
    { id: '4', name: 'You', avatar: 'U', isOnline: true, isMuted: false },
  ]);
  const [isMuted, setIsMuted] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [showMembersPopup, setShowMembersPopup] = useState(false);
  const [showBreakReminder, setShowBreakReminder] = useState(false);
  const missedBreakHandledRef = useRef(false);
  const [currentBreakReminder, setCurrentBreakReminder] = useState<any>(null);
  const [breakStarted, setBreakStarted] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showMicroReminder, setShowMicroReminder] = useState(false);
  const [microReminderShown, setMicroReminderShown] = useState(false);
  const [breakSkipped, setBreakSkipped] = useState(false);
  const [breakTimeLeft, setBreakTimeLeft] = useState(0);
  const [missedBreakCount, setMissedBreakCount] = useState(0);

  const [sharedTasks, setSharedTasks] = useState<
    Array<{
      id: string;
      text: string;
      completed: boolean;
      createdBy: string;
      assignedTo?: { id: string; name: string; avatar: string };
      createdAt: Date;
    }>
  >([]);
  const [sharedNotes, setSharedNotes] = useState<
    Array<{
      id: string;
      text: string;
      createdBy: string;
      createdAt: Date;
    }>
  >([]);
  const [showEndSessionModal, setShowEndSessionModal] = useState(false);
  const [showPreSessionModal, setShowPreSessionModal] = useState(false);
  const [sessionTab, setSessionTab] = useState<
    'teams' | 'notes' | 'tasks' | 'reminders'
  >('notes');
  const [showTasksTooltip, setShowTasksTooltip] = useState(false);
  const [formResetKey, setFormResetKey] = useState(0);
  const [showProjectRequiredAlert, setShowProjectRequiredAlert] =
    useState(false);
  const [pendingTeamJoinStart, setPendingTeamJoinStart] = useState(false);
  const [breakRemindersEnabled, setBreakRemindersEnabled] = useState(true);
  const [musicVolume, setMusicVolume] = useState(50);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [isMusicMuted, setIsMusicMuted] = useState(false);

  // Audio refs for music tracks
  const noiseAudioRef = useRef<HTMLAudioElement | null>(null);
  const natureAudioRef = useRef<HTMLAudioElement | null>(null);
  const binauralAudioRef = useRef<HTMLAudioElement | null>(null);

  // YouTube player ref
  const youtubePlayerRef = useRef<any>(null);
  const screenShareStreamRef = useRef<MediaStream | null>(null);
  const youtubePlayerContainerRef = useRef<HTMLDivElement | null>(null);
  const [youtubeApiReady, setYoutubeApiReady] = useState(false);

  // Ref to store endSession function for navigation prevention
  const endSessionRef = useRef<(() => void) | null>(null);
  // Ref to track if end warning has been shown
  const endWarningShownRef = useRef(false);
  // Refs to track session state for navigation prevention
  const sessionStartedRef = useRef(false);
  const sessionCompletedRef = useRef(false);

  // Utility function to extract YouTube video ID from URL
  const extractYouTubeVideoId = (url: string): string | null => {
    if (!url) return null;

    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    return null;
  };

  // Initialize YouTube API
  useEffect(() => {
    // Check if YouTube API is already loaded
    if ((window as any).YT && (window as any).YT.Player) {
      setYoutubeApiReady(true);
      return;
    }

    // Wait for YouTube API to load
    const checkYoutubeAPI = setInterval(() => {
      if ((window as any).YT && (window as any).YT.Player) {
        setYoutubeApiReady(true);
        clearInterval(checkYoutubeAPI);
      }
    }, 100);

    // Set up global callback for YouTube API
    (window as any).onYouTubeIframeAPIReady = () => {
      setYoutubeApiReady(true);
      clearInterval(checkYoutubeAPI);
    };

    return () => {
      clearInterval(checkYoutubeAPI);
    };
  }, []);

  // Initialize YouTube player when custom music is selected
  useEffect(() => {
    if (!youtubeApiReady || !youtubePlayerContainerRef.current) return;

    const music = currentSession?.music || sessionData.music;
    const customMusicURL =
      currentSession?.customMusicURL || sessionData.customMusicURL;

    if (music === 'custom' && customMusicURL) {
      // IMPORTANT: Stop all default audio tracks when custom music is selected
      if (noiseAudioRef.current) {
        noiseAudioRef.current.pause();
        noiseAudioRef.current.currentTime = 0;
      }
      if (natureAudioRef.current) {
        natureAudioRef.current.pause();
        natureAudioRef.current.currentTime = 0;
      }
      if (binauralAudioRef.current) {
        binauralAudioRef.current.pause();
        binauralAudioRef.current.currentTime = 0;
      }

      const videoId = extractYouTubeVideoId(customMusicURL);

      if (videoId) {
        // Check if player exists and has the same video
        if (youtubePlayerRef.current) {
          try {
            const currentVideoId =
              youtubePlayerRef.current.getVideoData()?.video_id;
            if (currentVideoId === videoId) {
              // Player already has the correct video, just update volume
              youtubePlayerRef.current.setVolume(musicVolume);
              return;
            } else {
              // Different video, load new one
              youtubePlayerRef.current.loadVideoById(videoId);
              youtubePlayerRef.current.setVolume(musicVolume);
              return;
            }
          } catch (error) {
            console.error('Error checking YouTube player state:', error);
            // If there's an error, destroy and recreate
            try {
              youtubePlayerRef.current.destroy();
              youtubePlayerRef.current = null;
            } catch (destroyError) {
              console.error('Error destroying YouTube player:', destroyError);
            }
          }
        }

        // Create new player
        if (!youtubePlayerRef.current) {
          try {
            youtubePlayerRef.current = new (window as any).YT.Player(
              youtubePlayerContainerRef.current,
              {
                videoId: videoId,
                playerVars: {
                  autoplay: 0,
                  controls: 0,
                  disablekb: 1,
                  enablejsapi: 1,
                  fs: 0,
                  iv_load_policy: 3,
                  loop: 1,
                  playlist: videoId, // Required for looping
                  modestbranding: 1,
                  playsinline: 1,
                  rel: 0,
                  showinfo: 0,
                },
                events: {
                  onReady: (event: any) => {
                    console.log('YouTube player ready');
                    // Set initial volume
                    event.target.setVolume(musicVolume);
                    // Autoplay if session is already started
                    if (sessionStarted && !sessionPaused) {
                      setTimeout(() => {
                        try {
                          event.target.playVideo();
                        } catch (error) {
                          console.error(
                            'Error autoplaying YouTube video:',
                            error,
                          );
                        }
                      }, 100);
                    }
                  },
                  onStateChange: (event: any) => {
                    // Track playing state
                    const YT = (window as any).YT;
                    if (event.data === YT.PlayerState.PLAYING) {
                      setIsMusicPlaying(true);
                    } else if (
                      event.data === YT.PlayerState.PAUSED ||
                      event.data === YT.PlayerState.ENDED
                    ) {
                      setIsMusicPlaying(false);
                      if (event.data === YT.PlayerState.ENDED) {
                        event.target.playVideo(); // Loop the video
                      }
                    }
                    // Update muted state
                    try {
                      const isMuted = event.target.isMuted();
                      setIsMusicMuted(isMuted);
                    } catch (error) {
                      // Ignore errors
                    }
                  },
                  onError: (event: any) => {
                    console.error('YouTube player error:', event.data);
                  },
                },
              },
            );
          } catch (error) {
            console.error('Error initializing YouTube player:', error);
          }
        }
      }
    } else {
      // Destroy player if not using custom music
      if (youtubePlayerRef.current) {
        try {
          youtubePlayerRef.current.stopVideo();
          youtubePlayerRef.current.destroy();
          youtubePlayerRef.current = null;
        } catch (error) {
          console.error('Error destroying YouTube player:', error);
        }
      }
    }
  }, [
    youtubeApiReady,
    currentSession?.music,
    currentSession?.customMusicURL,
    sessionData.music,
    sessionData.customMusicURL,
  ]);

  // Update YouTube player volume when musicVolume changes
  useEffect(() => {
    if (
      youtubePlayerRef.current &&
      (currentSession?.music === 'custom' || sessionData.music === 'custom')
    ) {
      try {
        youtubePlayerRef.current.setVolume(musicVolume);
      } catch (error) {
        console.error('Error setting YouTube volume:', error);
      }
    }
  }, [musicVolume, currentSession?.music, sessionData.music]);

  // Break Reminder Data
  const breakReminders = [
    {
      id: 'eye-exercises',
      category: 'Eye Care',
      title: 'Eye Movement Exercise',
      description: 'Give your eyes a break from the screen',
      instructions: [
        'Look away from your screen',
        'Focus on an object 20 feet away for 20 seconds',
        'Slowly move your eyes in a figure-8 pattern',
        'Blink slowly 10 times',
        'Close your eyes and gently massage your eyelids',
      ],
      duration: '2 minutes',
      icon: <FaEye className='text-orange-500' />,
    },
    {
      id: 'neck-stretch',
      category: 'Stretching',
      title: 'Neck & Shoulder Stretch',
      description: 'Relieve tension in your neck and shoulders',
      instructions: [
        'Sit up straight in your chair',
        'Slowly tilt your head to the right, hold for 15 seconds',
        'Tilt your head to the left, hold for 15 seconds',
        'Gently roll your shoulders backward 5 times',
        'Roll your shoulders forward 5 times',
      ],
      duration: '3 minutes',
      icon: <MdAccessibilityNew className='text-orange-500' />,
    },
    {
      id: 'breathing-exercise',
      category: 'Mindfulness',
      title: 'Deep Breathing',
      description: 'Reset your mind with conscious breathing',
      instructions: [
        'Sit comfortably with your back straight',
        'Inhale slowly through your nose for 4 counts',
        'Hold your breath for 4 counts',
        'Exhale slowly through your mouth for 6 counts',
        'Repeat this cycle 5 times',
      ],
      duration: '2 minutes',
      icon: <RiMentalHealthLine className='text-orange-500' />,
    },
    {
      id: 'wrist-stretch',
      category: 'Stretching',
      title: 'Wrist & Hand Stretch',
      description: 'Prevent repetitive strain injury',
      instructions: [
        'Extend your arms in front of you',
        'Flex your wrists downward, hold for 15 seconds',
        'Extend your wrists upward, hold for 15 seconds',
        'Make gentle circles with your wrists',
        'Stretch your fingers wide apart and hold',
      ],
      duration: '2 minutes',
      icon: <FaHandPaper className='text-orange-500' />,
    },
    {
      id: 'back-stretch',
      category: 'Stretching',
      title: 'Back & Spine Stretch',
      description: 'Maintain good posture and spinal health',
      instructions: [
        'Stand up and place your hands on your hips',
        'Gently arch your back and look up',
        'Hold for 10 seconds, then round your back',
        'Reach your arms overhead and stretch upward',
        'Gently twist your torso left and right',
      ],
      duration: '3 minutes',
      icon: <FaChild className='text-orange-500' />,
    },
    {
      id: 'hydration-reminder',
      category: 'Wellness',
      title: 'Hydration Break',
      description: 'Stay hydrated for optimal brain function',
      instructions: [
        'Take a sip of water',
        'Stand up and walk to get water if needed',
        'Take 3 deep breaths while drinking',
        'Stretch your arms overhead',
        'Return to your work refreshed',
      ],
      duration: '1 minute',
      icon: <FaTint className='text-orange-500' />,
    },
    {
      id: 'mindfulness-moment',
      category: 'Mindfulness',
      title: 'Mindfulness Moment',
      description: 'Center yourself and refocus your attention',
      instructions: [
        'Close your eyes and take 3 deep breaths',
        'Notice how your body feels right now',
        'Acknowledge any tension and let it go',
        'Set an intention for the next focus period',
        'Open your eyes and return to work mindfully',
      ],
      duration: '2 minutes',
      icon: <FaSpa className='text-orange-500' />,
    },
    {
      id: 'walking-break',
      category: 'Movement',
      title: 'Mini Walking Break',
      description: 'Get your blood flowing with gentle movement',
      instructions: [
        'Stand up and walk around your space',
        'Take 10 steps in one direction',
        'Turn around and walk back',
        'Do this 3 times',
        'Stretch your legs and return to your seat',
      ],
      duration: '2 minutes',
      icon: <FaWalking className='text-orange-500' />,
    },
  ];

  // Helper function to get session duration in minutes
  const getSessionDuration = (): number => {
    // If session has started and we have a numeric duration, use that (handles extensions)
    if (sessionStarted && sessionData.duration) {
      return sessionData.duration;
    }
    if (sessionData.customDuration) {
      return parseInt(sessionData.customDuration);
    }
    return sessionData.duration || currentSession?.duration || 25;
  };

  // Helper function to check if should show micro-reminder based on time left
  const shouldShowMicroReminder = (timeLeftInSeconds: number): boolean => {
    if (
      !sessionStarted ||
      sessionPaused ||
      breakSkipped ||
      microReminderShown ||
      !breakRemindersEnabled
    ) {
      return false;
    }

    const duration = getSessionDuration();
    const elapsedSeconds = duration * 60 - timeLeftInSeconds;
    const elapsedMinutes = Math.floor(elapsedSeconds / 60);

    // 45-minute session: show at 25-30 minutes (15-20 minutes remaining)
    if (duration === 45) {
      const timeRemaining = Math.floor(timeLeftInSeconds / 60);
      if (timeRemaining <= 20 && timeRemaining >= 15) {
        return true;
      }
    }

    // 60-minute session: show at 30 minutes (30 minutes remaining)
    if (duration === 60) {
      const timeRemaining = Math.floor(timeLeftInSeconds / 60);
      if (timeRemaining <= 30 && timeRemaining >= 28) {
        return true;
      }
    }

    return false;
  };

  // Helper function to check if should show full break at session end
  const shouldShowFullBreak = (): boolean => {
    if (breakSkipped || !sessionStarted || !breakRemindersEnabled) {
      return false;
    }

    // Only show at session end (when timeLeft <= 1)
    return sessionTimeLeft <= 1;
  };

  // Show micro-reminder (non-intrusive)
  const triggerMicroReminder = (timeLeftInSeconds: number) => {
    if (shouldShowMicroReminder(timeLeftInSeconds)) {
      setShowMicroReminder(true);
      setMicroReminderShown(true);
      // Auto-hide after 8 seconds
      setTimeout(() => {
        setShowMicroReminder(false);
      }, 8000);
    }
  };

  // Show full break modal at session end
  const showFullBreakAtEnd = () => {
    if (shouldShowFullBreak() && !breakSkipped) {
      // Always show eye exercise at session end
      const eyeExercise =
        breakReminders.find((r) => r.id === 'eye-exercises') ||
        breakReminders[0];
      setCurrentBreakReminder(eyeExercise);
      setBreakStarted(false);
      setShowBreakReminder(true);
    }
  };

  // Break Reminder Functions (legacy - kept for manual trigger)
  const showRandomBreakReminder = () => {
    // Only show break reminder if session is started and not already paused
    if (!sessionStarted || sessionPaused) {
      return;
    }
    const randomReminder =
      breakReminders[Math.floor(Math.random() * breakReminders.length)];
    setCurrentBreakReminder(randomReminder);
    setBreakStarted(false);
    setShowBreakReminder(true);
  };

  const startBreak = () => {
    // Pause the session (timer and music)
    if (sessionStarted && !sessionPaused) {
      pauseSession();
      setIsMusicPlaying(false);
    }
    setBreakStarted(true);

    // Initialize break timer
    if (currentBreakReminder) {
      // Parse duration usually "X minutes"
      const durationMatch =
        currentBreakReminder.duration.match(/(\d+)\s*minute/i);
      const minutes = durationMatch ? parseInt(durationMatch[1], 10) : 2;
      setBreakTimeLeft(minutes * 60);
    }

    // Start voice if enabled
    if (voiceEnabled && currentBreakReminder) {
      speakInstructions();
    }
  };

  // Text-to-speech function - only reads instructions, synced with break duration
  const speakInstructions = () => {
    if (!currentBreakReminder || !('speechSynthesis' in window)) return;

    setIsSpeaking(true);

    // Parse duration (e.g., "1 minute", "3 minutes", "2 minutes")
    const durationMatch =
      currentBreakReminder.duration.match(/(\d+)\s*minute/i);
    const durationMinutes = durationMatch ? parseInt(durationMatch[1], 10) : 2; // Default to 2 minutes
    const targetDurationSeconds = durationMinutes * 60;

    // Calculate total words in instructions
    let speechText = '';
    currentBreakReminder.instructions.forEach(
      (instruction: string, index: number) => {
        speechText += `Step ${index + 1}: ${instruction}. `;
      },
    );

    const wordCount = speechText.split(/\s+/).length;

    // Calculate speech rate to match duration
    // Average speaking rate is ~150 words per minute
    // We want: (wordCount / rate) * 60 = targetDurationSeconds
    // So: rate = (wordCount * 60) / targetDurationSeconds
    const baseRate = 0.9; // Base rate for clarity
    const calculatedRate = (wordCount * 60) / targetDurationSeconds;

    // Use calculated rate, but keep it within reasonable bounds (0.5 to 1.5)
    const finalRate = Math.max(0.5, Math.min(1.5, calculatedRate));

    // Add pauses between steps to better match duration
    let spacedSpeechText = '';
    currentBreakReminder.instructions.forEach(
      (instruction: string, index: number) => {
        spacedSpeechText += `Step ${index + 1}: ${instruction}. `;
        // Add a pause after each step (except the last one)
        if (index < currentBreakReminder.instructions.length - 1) {
          // Calculate pause duration: distribute remaining time across pauses
          const remainingTime =
            targetDurationSeconds - (wordCount * 60) / (finalRate * 150);
          const pausePerStep = Math.max(
            2,
            remainingTime / (currentBreakReminder.instructions.length - 1),
          );
          // Add pause markers (we'll use silent periods)
          spacedSpeechText += '... ';
        }
      },
    );

    const utterance = new SpeechSynthesisUtterance();
    utterance.text = spacedSpeechText;
    utterance.rate = finalRate;
    utterance.pitch = 1;
    utterance.volume = 1;

    // Add pauses between instructions using setTimeout
    let currentIndex = 0;
    const speakWithPauses = () => {
      if (currentIndex >= currentBreakReminder.instructions.length) {
        setIsSpeaking(false);
        return;
      }

      const instruction = currentBreakReminder.instructions[currentIndex];
      const stepText = `Step ${currentIndex + 1}: ${instruction}. `;

      const stepUtterance = new SpeechSynthesisUtterance(stepText);
      stepUtterance.rate = finalRate;
      stepUtterance.pitch = 1;
      stepUtterance.volume = 1;

      stepUtterance.onend = () => {
        currentIndex++;
        if (currentIndex < currentBreakReminder.instructions.length) {
          // Calculate pause duration to sync with total time
          const timePerStep =
            targetDurationSeconds / currentBreakReminder.instructions.length;
          const estimatedStepTime =
            (stepText.split(/\s+/).length * 60) / (finalRate * 150);
          const pauseTime = Math.max(
            1000,
            (timePerStep - estimatedStepTime) * 1000,
          );

          setTimeout(() => {
            speakWithPauses();
          }, pauseTime);
        } else {
          setIsSpeaking(false);
        }
      };

      stepUtterance.onerror = () => {
        setIsSpeaking(false);
      };

      window.speechSynthesis.speak(stepUtterance);
    };

    speakWithPauses();
  };

  const toggleVoice = () => {
    const newVoiceState = !voiceEnabled;
    setVoiceEnabled(newVoiceState);

    if (newVoiceState && breakStarted && currentBreakReminder) {
      // If enabling voice during active break, start speaking
      speakInstructions();
    } else if (!newVoiceState) {
      // If disabling voice, stop speaking
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  const closeBreakReminder = () => {
    // Stop any ongoing speech
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
    setShowBreakReminder(false);
    setCurrentBreakReminder(null);
    setBreakStarted(false);
    // Mark break as skipped to suppress future prompts
    setBreakSkipped(true);
    // Show completion modal if session ended
    if (sessionCompleted) {
      setShowCompletionModal(true);
    }
  };

  const continueSessionFromBreak = () => {
    // Resume the session (timer and music)
    if (sessionStarted && sessionPaused) {
      resumeSession();
      // isMusicPlaying will be set to true by resumeSession if music is playing
    }
    closeBreakReminder();
  };

  // Handle missed break
  const handleMissedBreak = useCallback(() => {
    if (missedBreakHandledRef.current) return;
    missedBreakHandledRef.current = true;
    if (currentBreakReminder) {
      const timeString = moment().format('h:mm A');

      setMissedBreakCount((prev) => prev + 1);

      toast.error(
        <div className='flex items-center justify-between gap-3 w-full'>
          <div>
            <p className='font-bold'>Missed Break</p>
            <p className='text-sm'>{currentBreakReminder.title}</p>
            <p className='text-xs opacity-75'>at {timeString}</p>
          </div>
          <div className='text-2xl mt-1'>{currentBreakReminder.icon}</div>
        </div>,
        {
          position: 'bottom-left',
          autoClose: false,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'colored',
          onClose: () => setMissedBreakCount((prev) => Math.max(0, prev - 1)),
        },
      );
    }
    closeBreakReminder();
  }, [currentBreakReminder]);

  // Auto-close timer for break reminder
  const [breakWarningTimer, setBreakWarningTimer] = useState(60);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (showBreakReminder && !breakStarted && breakWarningTimer > 0) {
      interval = setInterval(() => {
        setBreakWarningTimer((prev) => prev - 1);
      }, 1000);
    } else if (breakWarningTimer === 0 && showBreakReminder && !breakStarted) {
      // Timer finished, treat as missed break
      handleMissedBreak();
    }
    return () => clearInterval(interval);
  }, [showBreakReminder, breakStarted, breakWarningTimer, handleMissedBreak]);

  // Reset timer and ref when modal opens
  useEffect(() => {
    if (showBreakReminder) {
      setBreakWarningTimer(60);
      missedBreakHandledRef.current = false;
    }
  }, [showBreakReminder]);

  // Break Session Timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (breakStarted && breakTimeLeft > 0 && showBreakReminder) {
      interval = setInterval(() => {
        setBreakTimeLeft((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [breakStarted, breakTimeLeft, showBreakReminder]);

  // Reminders
  const [reminders, setReminders] = useState<
    Array<{
      id: string;
      text: string;
      time: string;
      completed: boolean;
    }>
  >([]);

  // Session Timer
  const [sessionTimer, setSessionTimer] = useState<NodeJS.Timeout | null>(null);

  // Restore persisted session from sessionStorage on mount
  useEffect(() => {
    const savedSession = sessionStorage.getItem('activeSession');
    if (savedSession) {
      try {
        const parsedSession = JSON.parse(savedSession);

        // Only restore if it was marked as active
        if (parsedSession.isActive) {
          // Check time elapsed since last update
          const timeSinceLastUpdate =
            Date.now() - (parsedSession.lastUpdated || 0);

          // Calculate corrected time left
          // If session was paused, time shouldn't decrease
          // If session was active, subtract the time that passed while offline/stale
          // If significant time passed (> 1 min) while inactive, ask user
          // If significant time passed (> 5s) while inactive, ask user
          // We use 5s threshold to match the active timer logic
          // if (timeSinceLastUpdate > 5000 && !parsedSession.isPaused) {
          //   setSessionTimeLeft(parsedSession.timeLeft);
          //   setSessionStarted(true);
          //   setSessionPaused(true); // Pause it until user decides
          //   setMissedSessionTime(Math.floor(timeSinceLastUpdate / 1000));
          //   setGapDetectionTime(Date.now());
          //   setShowResumeModal(true);
          // } else {
          // Auto-correct small drifts or if paused
          let correctedTimeLeft = parsedSession.timeLeft;
          if (!parsedSession.isPaused) {
            correctedTimeLeft = Math.max(
              0,
              parsedSession.timeLeft - Math.floor(timeSinceLastUpdate / 1000),
            );
          }
          setSessionTimeLeft(correctedTimeLeft);
          setSessionStarted(true);
          setSessionPaused(parsedSession.isPaused);
          // }

          // Restore mode and data
          if (parsedSession.mode) setActiveMode(parsedSession.mode);
          if (parsedSession.sessionData)
            setSessionData(parsedSession.sessionData);

          // Fetch the full session details from API to sync
          if (parsedSession.sessionId) {
            dispatch(fetchFocusSessionById(parsedSession.sessionId))
              .unwrap()
              .then((fetchedSession) => {
                if (fetchedSession) {
                  // Sync tasks from API if present
                  if (fetchedSession.tasks && fetchedSession.tasks.length > 0) {
                    const mappedTasks = fetchedSession.tasks.map((t, i) => ({
                      id: t.id || Date.now().toString(),
                      text: t.title || t.text, // Handle potential field naming diffs
                      title: t.title || t.text,
                      completed: t.completed,
                      status: t.status || (t.completed ? 'done' : 'todo'),
                      createdAt: t.createdAt || new Date(),
                      created_by: t.created_by,
                      assigned_to: t.assigned_to,
                      assignedTo: t.assignedTo,
                      is_public: t.is_public
                    }));
                    setTasks(mappedTasks);
                  }

                  // Sync notes from API if present
                  if (fetchedSession.notes && fetchedSession.notes.length > 0) {
                    const mappedNotes = (
                      fetchedSession.notes as Array<any>
                    ).map((n, i) => ({
                      id: `${Date.now()}-${i}`,
                      text: n,
                      createdAt: new Date(),
                    }));
                    setNotes(mappedNotes);
                    setSessionNotes(mappedNotes.map((n) => n.text));
                  }
                }
                setShowFocusSession(true);
              })
              .catch((error) => {
                console.error('Failed to sync session details:', error);
                // Still open the modal even if sync fails, using cached data
                setShowFocusSession(true);
              });
          } else {
            setShowFocusSession(true);
          }
        }
      } catch (error) {
        console.error('Failed to restore session:', error);
        sessionStorage.removeItem('activeSession');
      }
    }
  }, [dispatch]);

  // Detect room invite link from URL (?room=ABC123) - switch to Team mode and prefill join
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const room = params.get('room') || params.get('roomId') || params.get('code');
    if (room) {
      setActiveMode('team');
      setTeamTab('join');
      setRoomCode(room.toUpperCase());
      // Clean URL so refreshing doesn't re-trigger
      const url = new URL(window.location.href);
      url.searchParams.delete('room');
      url.searchParams.delete('roomId');
      url.searchParams.delete('code');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }, []);

  // After joining a room, sync sessionData and show PreSession modal
  useEffect(() => {
    if (!pendingTeamJoinStart) return;
    setPendingTeamJoinStart(false);
    if (sessionData.projectId) {
      startFocusSession();
    } else {
      setShowProjectRequiredAlert(true);
    }
  }, [pendingTeamJoinStart, sessionData.projectId]);

  // Track last tick time to detect sleep/interruptions
  const lastTickTimeRef = useRef<number>(Date.now());

  // Helper to finish session (used by timer and modal)
  const finishSession = useCallback(() => {
    screenShareStreamRef.current?.getTracks().forEach((t) => t.stop());
    screenShareStreamRef.current = null;
    setIsScreenSharing(false);
    const intendedDuration =
      currentSession?.duration ||
      (sessionData.customDuration
        ? parseInt(sessionData.customDuration)
        : sessionData.duration) ||
      25;

    setCompletedSessionDuration(intendedDuration);
    setSessionStarted(false);
    setSessionPaused(false);
    setSessionCompleted(true);
    dispatch(setCurrentSession(null));
    setEndWarningShown(false);
    setShowEndWarningMessage(false);
    endWarningShownRef.current = false;
    sessionStorage.removeItem('activeSession');
    setShowCompletionModal(true);

    if (noiseAudioRef.current) {
      noiseAudioRef.current.pause();
      noiseAudioRef.current.currentTime = 0;
    }
    if (natureAudioRef.current) {
      natureAudioRef.current.pause();
      natureAudioRef.current.currentTime = 0;
    }
    if (binauralAudioRef.current) {
      binauralAudioRef.current.pause();
      binauralAudioRef.current.currentTime = 0;
    }
    if (youtubePlayerRef.current) {
      try {
        youtubePlayerRef.current.stopVideo();
        youtubePlayerRef.current.destroy();
        youtubePlayerRef.current = null;
      } catch (e) {
        console.error('Error stopping YouTube player:', e);
      }
    }
    setIsMusicPlaying(false);
  }, [currentSession, sessionData, dispatch]);

  // Main Session Timer Effect
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (sessionStarted && !sessionPaused) {
      // Initialize last tick time
      lastTickTimeRef.current = Date.now();
      timer = setInterval(() => {
        const now = Date.now();
        const delta = now - lastTickTimeRef.current;
        // NOTE: We do NOT update lastTickTimeRef here immediately. We update it after success.

        // Detect if system slept or tab was severely throttled (gap > 5 seconds)
        // We use 5000ms threshold to ignore minor stutters but catch sleep
        if (delta > 5000) {
          // clearInterval(timer);
          // setSessionPaused(true);
          // setMissedSessionTime(Math.floor(delta / 1000));
          // setGapDetectionTime(Date.now());
          // setShowResumeModal(true);
          // return;
        }

        lastTickTimeRef.current = now;

        setSessionTimeLeft((prev) => {
          // Calculate remaining time based on delta decrement (prevents auto-syncing drift)
          const newTime = Math.max(0, prev - delta / 1000);

          // If time hasn't changed (due to high frequency interval), return prev
          if (newTime === prev) return prev;

          // Show end warning at configured time remaining
          // Check if we just crossed the threshold or exactly hit it
          if (
            newTime <= beepWarningTime &&
            prev > beepWarningTime &&
            !endWarningShownRef.current
          ) {
            endWarningShownRef.current = true;
            setEndWarningShown(true);
            setShowEndWarningMessage(true);

            // Play beep sound if enabled
            if (beepSoundEnabled) {
              console.log(
                'Playing beep sound at',
                beepWarningTime,
                'seconds remaining',
              );
              playBeepSound();
            } else {
              console.log('Beep sound is disabled');
            }

            // Show browser notification if permission granted
            if (
              'Notification' in window &&
              Notification.permission === 'granted'
            ) {
              try {
                new Notification('Focus Session Ending Soon', {
                  body: `Your focus session will end in ${beepWarningTime} seconds. Time to wrap up!`,
                  icon: '/focuzon.png',
                  tag: 'focus-session-end',
                  requireInteraction: false,
                });
              } catch (error) {
                console.error('Error showing notification:', error);
              }
            }

            // Auto-hide message after 5 seconds
            setTimeout(() => {
              setShowEndWarningMessage(false);
            }, 5000);
          }

          // Check for periodic break reminders (every 25 minutes of elapsed time)
          if (breakRemindersEnabled) {
            // Auto-disable if remaining time is less than 25 minutes
            if (newTime < 1500 && prev >= 1500) {
              setTimeout(() => {
                setBreakRemindersEnabled(false);
              }, 0);
            }

            const durationInSeconds = getSessionDuration() * 60;
            const elapsedSeconds = durationInSeconds - newTime;
            const prevElapsedSeconds = durationInSeconds - prev;

            // Trigger at 25m (1500s), 50m (3000s), etc.
            // Check if we crossed a 1500s boundary
            const crossedBreakThreshold =
              Math.floor(elapsedSeconds / 1500) >
              Math.floor(prevElapsedSeconds / 1500);

            if (elapsedSeconds > 0 && crossedBreakThreshold) {
              // Use existing showRandomBreakReminder logic but safely invocation
              // We need to call it outside the state update or ensure it's safe.
              // Check if modal is already open to avoid overwriting/resetting
              if (!showBreakReminder) {
                setTimeout(() => {
                  const randomReminder =
                    breakReminders[
                    Math.floor(Math.random() * breakReminders.length)
                    ];
                  setCurrentBreakReminder(randomReminder);
                  setShowBreakReminder(true);
                  // Play a sound for break?
                  if (beepSoundEnabled) playBeepSound();
                }, 0);
              }
            }
          }

          if (newTime <= 0) {
            clearInterval(timer);
            finishSession();
            return 0;
          }

          return newTime;
        });
      }, 1000);

      setSessionTimer(timer);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [
    sessionStarted,
    sessionPaused,
    beepWarningTime,
    beepSoundEnabled,
    breakRemindersEnabled,
    finishSession,
  ]);

  // Persist active session state
  useEffect(() => {
    if (sessionStarted && !sessionPaused && currentSession) {
      sessionStorage.setItem(
        'activeSession',
        JSON.stringify({
          timeLeft: sessionTimeLeft,
          sessionId: currentSession.id,
          isActive: true,
          isPaused: false,
          lastUpdated: Date.now(),
          totalDuration: sessionData.duration * 60,
          mode: activeMode,
          sessionData: sessionData,
        }),
      );
    } else if (sessionPaused && currentSession && sessionStarted) {
      // Also update when paused to keep state valid, just mark as paused
      // This is crucial so if user refreshes while paused, it remembers
      sessionStorage.setItem(
        'activeSession',
        JSON.stringify({
          timeLeft: sessionTimeLeft,
          sessionId: currentSession.id,
          isActive: true,
          isPaused: true,
          lastUpdated: Date.now(),
          totalDuration: sessionData.duration * 60,
          mode: activeMode,
          sessionData: sessionData,
        }),
      );
    }
  }, [
    sessionTimeLeft,
    sessionStarted,
    sessionPaused,
    currentSession,
    activeMode,
    sessionData,
  ]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  // Fetch sessions on mount
  useEffect(() => {
    dispatch(fetchAllFocusSessions());
    dispatch(fetchGameStats());
    dispatch(
      fetchFocusStats({
        type: AnalyticsType.WEEKLY,
        projectId: sessionData.projectId,
      }),
    );
    dispatch(fetchGameAnalytics());
  }, [dispatch, sessionData.projectId]);

  // Track session start time
  const sessionStartTimeRef = useRef<number | null>(null);
  const pausedTimeRef = useRef<number>(0);
  const lastPauseTimeRef = useRef<number | null>(null);
  const projectSelectorRef = useRef<ProjectSelectorHandle>(null);

  const resumeModalOpenTimeRef = useRef<number>(0);

  // Update refs when session state changes
  useEffect(() => {
    sessionStartedRef.current = sessionStarted;
    sessionCompletedRef.current = sessionCompleted;
  }, [sessionStarted, sessionCompleted]);

  // Close tasks tooltip when any modal opens
  useEffect(() => {
    if (
      showAllSessions ||
      showProjectManagementModal ||
      showNoteModal ||
      showCompletionModal ||
      showSessionDetailsModal ||
      showPreSessionModal ||
      showEndSessionModal
    ) {
      setShowTasksTooltip(false);
    }
  }, [
    showAllSessions,
    showProjectManagementModal,
    showNoteModal,
    showCompletionModal,
    showSessionDetailsModal,
    showPreSessionModal,
    showEndSessionModal,
  ]);

  // Lock scroll for inline modals (others handle it internally)
  useScrollLock(showBreakReminder);

  // Cleanup speech when modal closes
  useEffect(() => {
    if (!showBreakReminder) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  }, [showBreakReminder]);

  // Prevent body scroll when pre-session modal is open

  // Filter and search sessions
  const filteredSessions = sessions.filter((session) => {
    const matchesFilter =
      sessionFilter === 'all' || session.type === sessionFilter;
    const notesText = Array.isArray(session.notes)
      ? session.notes.join(' ')
      : session.notes || '';
    const matchesSearch =
      session.goal.toLowerCase().includes(sessionSearch.toLowerCase()) ||
      notesText.toLowerCase().includes(sessionSearch.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Recent sessions (first 4) - convert to format expected by RecentSessions component
  const recentSessions = sessions.slice(0, 4).map((session) => ({
    id: session.id,
    type: session.type,
    goal: session.goal,
    duration: session.duration,
    completedAt: session.completedAt || session.updatedAt,
    notes: Array.isArray(session.notes)
      ? session.notes.join(' ')
      : session.notes || '',
    members: session.members || [],
    xp: session.xp || 0,
    music: session.music,
    tasks: session.tasks || [],
  }));

  // Greeting messages array
  const greetingMessages = [
    'Always nice to see you.',
    'You just made my day brighter.',
    "You've got that smile energy today.",
    'Glad to bump into you.',
    "Hope you're feeling awesome.",
    "It's always a pleasure seeing you.",
    'You look full of good vibes today.',
    'You make the place feel better.',
  ];


  // Badge system
  const availableBadges = [
    {
      id: 'stress-buster',
      name: 'Stress Buster',
      emoji: '💪',
      description: 'Completed 5 frustration games',
    },
    {
      id: 'zen-master',
      name: 'Zen Master',
      emoji: '🧘',
      description: 'Completed 10 calming games',
    },
    {
      id: 'refocus-pro',
      name: 'Refocus Pro',
      emoji: '🎯',
      description: 'Completed 15 cognitive games',
    },
    {
      id: 'puzzle-solver',
      name: 'Puzzle Solver',
      emoji: '🧩',
      description: 'Completed 10 puzzle games',
    },
    {
      id: 'focus-champion',
      name: 'Focus Champion',
      emoji: '🏆',
      description: 'Played 50 total games',
    },
    {
      id: 'mindful-minutes',
      name: 'Mindful Minutes',
      emoji: '⏰',
      description: 'Spent 2+ hours in games',
    },
  ];

  // Set random greeting message on component mount
  useEffect(() => {
    const randomMessage =
      greetingMessages[Math.floor(Math.random() * greetingMessages.length)];
    setGreetingMessage(randomMessage);
  }, []);

  // Update audio volume when musicVolume changes
  useEffect(() => {
    if (noiseAudioRef.current) {
      noiseAudioRef.current.volume = musicVolume / 100;
    }
    if (natureAudioRef.current) {
      natureAudioRef.current.volume = musicVolume / 100;
    }
    if (binauralAudioRef.current) {
      binauralAudioRef.current.volume = musicVolume / 100;
    }
  }, [musicVolume]);

  // Track audio playing state and muted state for all music types
  useEffect(() => {
    const music = currentSession?.music || sessionData.music;

    const updatePlayingState = () => {
      if (music === 'noise' && noiseAudioRef.current) {
        setIsMusicPlaying(!noiseAudioRef.current.paused);
        setIsMusicMuted(noiseAudioRef.current.muted);
      } else if (music === 'nature' && natureAudioRef.current) {
        setIsMusicPlaying(!natureAudioRef.current.paused);
        setIsMusicMuted(natureAudioRef.current.muted);
      } else if (music === 'binaural' && binauralAudioRef.current) {
        setIsMusicPlaying(!binauralAudioRef.current.paused);
        setIsMusicMuted(binauralAudioRef.current.muted);
      } else if (music === 'silence') {
        setIsMusicPlaying(false);
        setIsMusicMuted(false);
      } else if (music === 'custom') {
        // YouTube player state is tracked in onStateChange, but also poll here
        if (youtubePlayerRef.current) {
          try {
            const playerState = youtubePlayerRef.current.getPlayerState();
            const YT = (window as any).YT;
            setIsMusicPlaying(playerState === YT.PlayerState.PLAYING);
            // Check if YouTube player is muted
            const isMuted = youtubePlayerRef.current.isMuted();
            setIsMusicMuted(isMuted);
          } catch (error) {
            setIsMusicPlaying(false);
            setIsMusicMuted(false);
          }
        } else {
          setIsMusicPlaying(false);
          setIsMusicMuted(false);
        }
      }
    };

    // Check initial state
    updatePlayingState();

    // Set up event listeners for audio elements
    const noiseAudio = noiseAudioRef.current;
    const natureAudio = natureAudioRef.current;
    const binauralAudio = binauralAudioRef.current;

    if (noiseAudio) {
      noiseAudio.addEventListener('play', updatePlayingState);
      noiseAudio.addEventListener('pause', updatePlayingState);
      noiseAudio.addEventListener('ended', updatePlayingState);
    }
    if (natureAudio) {
      natureAudio.addEventListener('play', updatePlayingState);
      natureAudio.addEventListener('pause', updatePlayingState);
      natureAudio.addEventListener('ended', updatePlayingState);
    }
    if (binauralAudio) {
      binauralAudio.addEventListener('play', updatePlayingState);
      binauralAudio.addEventListener('pause', updatePlayingState);
      binauralAudio.addEventListener('ended', updatePlayingState);
    }

    // Poll YouTube player state periodically (since we can't easily listen to all state changes)
    let youtubePollInterval: NodeJS.Timeout | null = null;
    if (music === 'custom' && youtubePlayerRef.current) {
      youtubePollInterval = setInterval(() => {
        if (youtubePlayerRef.current) {
          try {
            const playerState = youtubePlayerRef.current.getPlayerState();
            const YT = (window as any).YT;
            setIsMusicPlaying(playerState === YT.PlayerState.PLAYING);
            // Also check muted state
            const isMuted = youtubePlayerRef.current.isMuted();
            setIsMusicMuted(isMuted);
          } catch (error) {
            // Ignore errors
          }
        }
      }, 500);
    }

    return () => {
      if (noiseAudio) {
        noiseAudio.removeEventListener('play', updatePlayingState);
        noiseAudio.removeEventListener('pause', updatePlayingState);
        noiseAudio.removeEventListener('ended', updatePlayingState);
      }
      if (natureAudio) {
        natureAudio.removeEventListener('play', updatePlayingState);
        natureAudio.removeEventListener('pause', updatePlayingState);
        natureAudio.removeEventListener('ended', updatePlayingState);
      }
      if (binauralAudio) {
        binauralAudio.removeEventListener('play', updatePlayingState);
        binauralAudio.removeEventListener('pause', updatePlayingState);
        binauralAudio.removeEventListener('ended', updatePlayingState);
      }
      if (youtubePollInterval) {
        clearInterval(youtubePollInterval);
      }
    };
  }, [currentSession?.music, sessionData.music]);

  const clearSessionData = () => {
    setSessionData({
      goal: '',
      duration: 25,
      customDuration: '',
      breakReminders: true,
      music: 'silence',
      roomName: '',
      roomCode: '',
      projectId: undefined,
      tasks: [],
    });
    setFormResetKey((prev) => prev + 1);
  };

  // Focus Session Functions
  const startFocusSession = () => {
    // Validate project selection
    if (!sessionData.projectId) {
      if (projectSelectorRef.current) {
        projectSelectorRef.current.open();
      } else {
        setShowProjectRequiredAlert(true);
      }
      return;
    }

    // Reset break reminder states for new session
    setBreakSkipped(false);
    setMicroReminderShown(false);
    setShowMicroReminder(false);
    setShowBreakReminder(false);
    const duration = sessionData.customDuration
      ? parseInt(sessionData.customDuration)
      : sessionData.duration;

    // Show pre-session modal
    setShowPreSessionModal(true);
  };

  const confirmPreSession = async () => {
    console.log('confirmPreSession called with sessionData:', sessionData);
    const duration = sessionData.customDuration
      ? parseInt(sessionData.customDuration)
      : sessionData.duration;

    if (!sessionData.projectId) {
      console.warn('No project selected, opening project selector');
      setShowPreSessionModal(false);
      if (projectSelectorRef.current) {
        projectSelectorRef.current.open();
      } else {
        setShowProjectRequiredAlert(true);
      }
      return;
    }

    // Log session data for debugging
    console.log('Creating session with sessionData:', {
      projectId: sessionData.projectId,
      tasks: sessionData.tasks,
      tasksCount: sessionData.tasks?.length || 0,
      goal: sessionData.goal,
      duration: duration,
    });

    // Create focus session via API (team mode has no focus sounds - always silence)
    try {
      const createData = {
        goal: sessionData.goal || 'Focus Session',
        duration: duration,
        breakReminders: sessionData.breakReminders,
        music: (activeMode === 'team' ? 'silence' : sessionData.music) as FocusMusicType,
        ...(activeMode === 'solo' &&
          sessionData.music === 'custom' &&
          sessionData.customMusicURL
          ? { customMusicURL: sessionData.customMusicURL }
          : {}),
        startTime: new Date().toISOString(),

        ...(activeMode === 'team' &&
          sessionData.roomCode && { roomId: sessionData.roomCode }),
        projectId: sessionData.projectId, // Required field
        ...(sessionData.tasks &&
          sessionData.tasks.length > 0 && { tasks: sessionData.tasks }),
      };

      console.log('Sending createData to API:', createData);

      if (activeMode === 'team') {

        const response = await api.teamMode.createSession(createData);
        if (response.id) {
          const teamId = response.roomId;
          console.log('Team session created successfully with ID:', response.roomId);
          // forward the full session object so page doesn't re-fetch
          navigate(`/team/${teamId}`, { state: { teamSession: response } });
        }
        //TODO: handle error
        return;
      }

      // solo mode: create via redux thunk and grab result
      const result = await dispatch(createFocusSession(createData)).unwrap();
      console.log('Session created successfully:', result);

      // Only proceed if session was created successfully
      dispatch(setCurrentSession(result));
      setSessionTimeLeft(duration * 60); // Convert to seconds
      setShowFocusSession(true);
      setShowPreSessionModal(false);
      setSessionStarted(true);
      setSessionPaused(false);
      setSessionCompleted(false);
      // Initialize break reminders from session data
      setBreakRemindersEnabled(sessionData.breakReminders ?? true);
      // Set Tasks tab as default and show tooltip (only if enabled in settings)
      setSessionTab('tasks');
      const tasksTooltipEnabled = storage.get<boolean>(
        'tasksTooltipEnabled',
        true,
      );
      if (tasksTooltipEnabled !== null && tasksTooltipEnabled) {
        setShowTasksTooltip(true);
      }
      // Reset end warning state
      setEndWarningShown(false);
      setShowEndWarningMessage(false);
      endWarningShownRef.current = false;
      // Reset break reminder states for new session
      setBreakSkipped(false);
      setMicroReminderShown(false);
      setShowMicroReminder(false);
      setShowBreakReminder(false);
      // Initialize sessionNotes as empty array
      setSessionNotes([]);
      setNotes([]);
      setReminders([]);
      // Initialize tasks from sessionData (convert from Task[] to UITask[])
      const initialTasks: UITask[] = (sessionData.tasks || []).map(
        (task, index) => ({
          id: task.id || `pre-session-${Date.now()}-${index}`,
          text: task.title || task.text,
          title: task.title || task.text,
          completed: task.completed,
          status: task.status || (task.completed ? 'done' : 'todo'),
          createdAt: task.createdAt || new Date(),
          created_by: task.created_by,
          assigned_to: task.assigned_to,
          assignedTo: task.assignedTo,
          is_public: task.is_public
        }),
      );
      setTasks(initialTasks);
      setNewNote(''); // Clear note input field

      // Clear session input data after session is created

      // Stop any previously playing audio
      if (noiseAudioRef.current) {
        noiseAudioRef.current.pause();
        noiseAudioRef.current.currentTime = 0;
      }
      if (natureAudioRef.current) {
        natureAudioRef.current.pause();
        natureAudioRef.current.currentTime = 0;
      }
      if (binauralAudioRef.current) {
        binauralAudioRef.current.pause();
        binauralAudioRef.current.currentTime = 0;
      }

      // Get the current music selection from currentSession or sessionData
      const currentMusic = currentSession?.music || sessionData.music;

      // Start noise audio if selected
      if (currentMusic === 'noise' && noiseAudioRef.current) {
        // Ensure other audio tracks and YouTube are stopped
        if (natureAudioRef.current) {
          natureAudioRef.current.pause();
          natureAudioRef.current.currentTime = 0;
        }
        if (binauralAudioRef.current) {
          binauralAudioRef.current.pause();
          binauralAudioRef.current.currentTime = 0;
        }
        if (youtubePlayerRef.current) {
          try {
            youtubePlayerRef.current.pauseVideo();
          } catch (error) {
            console.error('Error pausing YouTube player:', error);
          }
        }
        noiseAudioRef.current.loop = true;
        noiseAudioRef.current.volume = musicVolume / 100;
        noiseAudioRef.current.play().catch((error) => {
          console.error('Error playing noise audio:', error);
        });
      }

      // Start nature audio if selected
      if (currentMusic === 'nature' && natureAudioRef.current) {
        // Ensure other audio tracks and YouTube are stopped
        if (noiseAudioRef.current) {
          noiseAudioRef.current.pause();
          noiseAudioRef.current.currentTime = 0;
        }
        if (binauralAudioRef.current) {
          binauralAudioRef.current.pause();
          binauralAudioRef.current.currentTime = 0;
        }
        if (youtubePlayerRef.current) {
          try {
            youtubePlayerRef.current.pauseVideo();
          } catch (error) {
            console.error('Error pausing YouTube player:', error);
          }
        }
        natureAudioRef.current.loop = true;
        natureAudioRef.current.volume = musicVolume / 100;
        natureAudioRef.current.play().catch((error) => {
          console.error('Error playing nature audio:', error);
        });
      }

      // Start binaural audio if selected
      if (currentMusic === 'binaural' && binauralAudioRef.current) {
        // Ensure other audio tracks and YouTube are stopped
        if (noiseAudioRef.current) {
          noiseAudioRef.current.pause();
          noiseAudioRef.current.currentTime = 0;
        }
        if (natureAudioRef.current) {
          natureAudioRef.current.pause();
          natureAudioRef.current.currentTime = 0;
        }
        if (youtubePlayerRef.current) {
          try {
            youtubePlayerRef.current.pauseVideo();
          } catch (error) {
            console.error('Error pausing YouTube player:', error);
          }
        }
        binauralAudioRef.current.loop = true;
        binauralAudioRef.current.volume = musicVolume / 100;
        binauralAudioRef.current.play().catch((error) => {
          console.error('Error playing binaural audio:', error);
        });
      }

      // Start YouTube player if custom music is selected
      if (currentMusic === 'custom') {
        // IMPORTANT: Stop all default audio tracks when using custom YouTube music
        if (noiseAudioRef.current) {
          noiseAudioRef.current.pause();
          noiseAudioRef.current.currentTime = 0;
        }
        if (natureAudioRef.current) {
          natureAudioRef.current.pause();
          natureAudioRef.current.currentTime = 0;
        }
        if (binauralAudioRef.current) {
          binauralAudioRef.current.pause();
          binauralAudioRef.current.currentTime = 0;
        }

        // Wait a bit for player to be ready, then play

        let retryCount = 0;
        const maxRetries = 15;

        const playYouTubeVideo = () => {
          if (youtubePlayerRef.current) {
            try {
              const playerState = youtubePlayerRef.current.getPlayerState();
              console.log('YouTube player state:', playerState);
              // Set volume first
              youtubePlayerRef.current.setVolume(musicVolume);
              // Try to play - YouTube API handles the state
              youtubePlayerRef.current.playVideo();
            } catch (error) {
              console.error('Error playing YouTube video:', error);
              // Retry after a delay
              setTimeout(() => {
                if (youtubePlayerRef.current) {
                  try {
                    youtubePlayerRef.current.setVolume(musicVolume);
                    youtubePlayerRef.current.playVideo();
                  } catch (retryError) {
                    console.error(
                      'Error retrying YouTube video play:',
                      retryError,
                    );
                  }
                }
              }, 1000);
            }
          } else {
            if (retryCount < maxRetries) {
              retryCount++;
              console.log(
                `YouTube player not initialized yet, waiting... (${retryCount}/${maxRetries})`,
              );
              // Player not initialized yet, wait and retry
              setTimeout(playYouTubeVideo, 1000);
            } else {
              console.warn(
                'YouTube player failed to initialize after multiple attempts',
              );
            }
          }
        };

        // Try to play immediately, or wait if player isn't ready
        if (youtubePlayerRef.current) {
          playYouTubeVideo();
        } else {
          // Wait a bit longer for initialization
          setTimeout(playYouTubeVideo, 1000);
        }
      }

      // Request notification permission
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }

      // Reset end warning state for new session
      setEndWarningShown(false);
      setShowEndWarningMessage(false);
      endWarningShownRef.current = false;

      // Initialize start time
      sessionStartTimeRef.current = Date.now();
      pausedTimeRef.current = 0;
      lastPauseTimeRef.current = null;

      // Start timer
      setSessionTimer(null); // Timer is handled by useEffect
    } catch (error: any) {
      console.error('Failed to create focus session:', error);
      const errorMessage =
        error?.message ||
        error?.response?.data?.message ||
        'Failed to start focus session. Please try again.';
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
      });
      setShowPreSessionModal(true);
    }
  };

  const pauseSession = () => {
    if (sessionTimer) {
      clearInterval(sessionTimer);
      setSessionTimer(null);
    }
    setSessionPaused(true);
    // Get the current music selection from currentSession or sessionData
    const currentMusic = currentSession?.music || sessionData.music;
    // Pause audio
    if (noiseAudioRef.current && currentMusic === 'noise') {
      noiseAudioRef.current.pause();
      setIsMusicPlaying(false);
    }
    if (natureAudioRef.current && currentMusic === 'nature') {
      natureAudioRef.current.pause();
      setIsMusicPlaying(false);
    }
    if (binauralAudioRef.current && currentMusic === 'binaural') {
      binauralAudioRef.current.pause();
      setIsMusicPlaying(false);
    }
    // Pause YouTube player
    if (currentMusic === 'custom' && youtubePlayerRef.current) {
      try {
        youtubePlayerRef.current.pauseVideo();
        setIsMusicPlaying(false);
      } catch (error) {
        console.error('Error pausing YouTube video:', error);
      }
    }
  };

  const resumeSession = () => {
    setSessionPaused(false);

    // Timer is handled by useEffect when sessionPaused becomes false

    // Resume audio
    const currentMusic = currentSession?.music || sessionData.music;
    if (noiseAudioRef.current && currentMusic === 'noise') {
      noiseAudioRef.current.play().catch(console.error);
      setIsMusicPlaying(true);
    }
    if (natureAudioRef.current && currentMusic === 'nature') {
      natureAudioRef.current.play().catch(console.error);
      setIsMusicPlaying(true);
    }
    if (binauralAudioRef.current && currentMusic === 'binaural') {
      binauralAudioRef.current.play().catch(console.error);
      setIsMusicPlaying(true);
    }
    if (currentMusic === 'custom' && youtubePlayerRef.current) {
      try {
        youtubePlayerRef.current.playVideo();
        setIsMusicPlaying(true);
      } catch (error) {
        console.error('Error resuming YouTube video:', error);
      }
    }
  };
  const handleChangeMusic = (newMusic: string) => {
    if (!sessionStarted || sessionCompleted) {
      return;
    }

    // If switching to custom music, check if we have a URL first
    // This prevents stopping current music or changing dropdown if URL is missing
    if (newMusic === 'custom') {
      const customUrl =
        currentSession?.customMusicURL || sessionData.customMusicURL;
      if (!customUrl) {
        // No URL set, show modal and stop here
        setShowCustomMusicModal(true);
        return;
      }
    }

    const musicType = newMusic as FocusMusicType;

    // Update session data
    setSessionData({
      ...sessionData,
      music: musicType,
    });

    // Update session via API
    if (currentSession) {
      dispatch(
        updateFocusSession({
          id: currentSession.id,
          data: {
            music: musicType,
          },
        }),
      ).catch((error) => {
        console.error('Failed to update session music:', error);
      });
    }

    // Stop all current music
    const wasPlaying = isMusicPlaying;
    if (noiseAudioRef.current) {
      noiseAudioRef.current.pause();
      noiseAudioRef.current.currentTime = 0;
    }
    if (natureAudioRef.current) {
      natureAudioRef.current.pause();
      natureAudioRef.current.currentTime = 0;
    }
    if (binauralAudioRef.current) {
      binauralAudioRef.current.pause();
      binauralAudioRef.current.currentTime = 0;
    }
    if (youtubePlayerRef.current) {
      try {
        youtubePlayerRef.current.pauseVideo();
      } catch (error) {
        console.error('Error pausing YouTube player:', error);
      }
    }
    setIsMusicPlaying(false);

    // Start new music if it was playing before OR if we are switching from silence
    const currentMusic = currentSession?.music || sessionData.music;
    const shouldPlay =
      wasPlaying || (currentMusic === 'silence' && newMusic !== 'silence');

    if (shouldPlay && newMusic !== 'silence' && !sessionPaused) {
      setTimeout(() => {
        if (newMusic === 'noise' && noiseAudioRef.current) {
          noiseAudioRef.current.volume = musicVolume / 100;
          noiseAudioRef.current.play().catch((error) => {
            console.error('Error playing noise audio:', error);
          });
          setIsMusicPlaying(true);
        } else if (newMusic === 'nature' && natureAudioRef.current) {
          natureAudioRef.current.volume = musicVolume / 100;
          natureAudioRef.current.play().catch((error) => {
            console.error('Error playing nature audio:', error);
          });
          setIsMusicPlaying(true);
        } else if (newMusic === 'binaural' && binauralAudioRef.current) {
          binauralAudioRef.current.volume = musicVolume / 100;
          binauralAudioRef.current.play().catch((error) => {
            console.error('Error playing binaural audio:', error);
          });
          setIsMusicPlaying(true);
        } else if (newMusic === 'custom' && youtubePlayerRef.current) {
          try {
            youtubePlayerRef.current.setVolume(musicVolume);
            youtubePlayerRef.current.playVideo();
            setIsMusicPlaying(true);
          } catch (error) {
            console.error('Error playing YouTube video:', error);
          }
        }
      }, 100);
    }
  };

  const extendSession = (minutes: number) => {
    if (!sessionStarted || sessionCompleted) {
      return;
    }
    // Add the minutes to the current time left
    setSessionTimeLeft((prev) => prev + minutes * 60);

    // Update local session data to reflect new duration
    // This ensures elapsed time calculations remain correct
    setSessionData((prev) => ({
      ...prev,
      duration: (prev.duration || 0) + minutes,
    }));

    // Update session duration in backend if session exists
    if (currentSession) {
      const currentDuration = currentSession.duration || sessionData.duration;
      const newDuration = currentDuration + minutes;

      dispatch(
        updateFocusSession({
          id: currentSession.id,
          data: {
            duration: newDuration,
          },
        }),
      ).catch((error) => {
        console.error('Failed to update session duration:', error);
      });
    }
  };

  const endSession = () => {
    // Don't show modal if session is already completed
    if (sessionCompleted) {
      return;
    }
    setShowEndSessionModal(true);
  };

  // Store endSession in ref for navigation prevention
  useEffect(() => {
    endSessionRef.current = endSession;
  }, [sessionCompleted]);

  const confirmEndSession = async () => {
    if (sessionTimer) {
      clearInterval(sessionTimer);
      setSessionTimer(null);
    }
    // Stop audio
    if (noiseAudioRef.current) {
      noiseAudioRef.current.pause();
      noiseAudioRef.current.currentTime = 0;
    }
    if (natureAudioRef.current) {
      natureAudioRef.current.pause();
      natureAudioRef.current.currentTime = 0;
    }
    if (binauralAudioRef.current) {
      binauralAudioRef.current.pause();
      binauralAudioRef.current.currentTime = 0;
    }
    // Stop and destroy YouTube player
    if (youtubePlayerRef.current) {
      try {
        youtubePlayerRef.current.stopVideo();
        youtubePlayerRef.current.destroy();
        youtubePlayerRef.current = null;
      } catch (error) {
        console.error('Error stopping/destroying YouTube video:', error);
      }
    }

    // Update session via API if it exists
    if (currentSession) {
      try {
        // Get user's intended duration (in minutes)
        const intendedDuration =
          currentSession.duration ||
          (sessionData.customDuration
            ? parseInt(sessionData.customDuration)
            : sessionData.duration) ||
          25;

        // Calculate actual completed duration (time spent)
        // intendedDuration is in minutes, sessionTimeLeft is in seconds
        // Ensure result is not negative (use Math.max(0, ...))
        const completedDurationInSeconds = Math.max(
          0,
          intendedDuration * 60 - sessionTimeLeft,
        );
        const completedDurationInMinutes = parseFloat(
          (completedDurationInSeconds / 60).toFixed(2),
        );

        await dispatch(
          updateFocusSession({
            id: currentSession.id,
            data: {
              duration: completedDurationInMinutes,
              endTime: new Date(),
              completed: true,
            },
          }),
        ).unwrap();
      } catch (error) {
        console.error('Failed to update session:', error);
      }
    }

    // Calculate actual duration completed
    const intendedDuration =
      currentSession?.duration ||
      (sessionData.customDuration
        ? parseInt(sessionData.customDuration)
        : sessionData.duration) ||
      25;
    const completedDurationInSeconds = Math.max(
      0,
      intendedDuration * 60 - sessionTimeLeft,
    );
    const completedDurationInMinutes = Math.round(
      completedDurationInSeconds / 60,
    );

    // Store completed duration for the modal
    setCompletedSessionDuration(completedDurationInMinutes);

    setShowFocusSession(false);
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    setSessionStarted(false);
    setSessionPaused(false);
    setSessionTimeLeft(0);
    setSessionCompleted(true); // Set to true to trigger completion modal
    setSessionNotes([]);
    setNotes([]);
    setTasks([]);
    setNewNote(''); // Clear note input field
    setReminders([]);
    setShowEndSessionModal(false);
    setShowTasksTooltip(false); // Hide tooltip when session ends
    sessionStorage.removeItem('activeSession');

    // Show completion modal
    setShowCompletionModal(true);
    dispatch(setCurrentSession(null));

    // Clear session input data when modal is closed
    clearSessionData();
  };

  const addTask = async (assignedTo?: { id: string; name: string; avatar: string } | null) => {
    if (newTask.trim()) {
      const assignee = assignedTo ?? undefined;
      const uiTask: UITask = {
        id: Date.now().toString(),
        text: newTask.trim(),
        completed: false,
        createdAt: new Date(),
        assignedTo: assignee,
      };

      const newTasksList = [...tasks, uiTask];
      setTasks(newTasksList);

      // Add to shared tasks if in team mode and sharing enabled
      const shareCheckbox = document.getElementById(
        'shareTask',
      ) as HTMLInputElement;
      if (shareCheckbox && shareCheckbox.checked) {
        addSharedTask(newTask.trim(), true, assignee);
      }

      setNewTask('');

      // Update session via API
      if (currentSession) {
        try {
          await dispatch(
            updateFocusSession({
              id: currentSession.id,
              data: {
                tasks: newTasksList.map((t) => ({
                  id: t.id,
                  title: t.title || t.text || 'Untitled Task',
                  status: t.status || (t.completed ? 'done' : 'todo'),
                  completed: t.completed,
                  createdAt: t.createdAt as string,
                  created_by: t.created_by || 'system',
                  is_public: t.is_public || false
                })),
              },
            }),
          ).unwrap();
        } catch (error) {
          console.error('Failed to update tasks:', error);
        }
      }
    }
  };

  const toggleTask = async (taskId: string) => {
    const updatedTasks = tasks.map((task) =>
      task.id === taskId ? { ...task, completed: !task.completed } : task,
    );
    setTasks(updatedTasks);

    // Update session via API
    if (currentSession) {
      try {
        await dispatch(
          updateFocusSession({
            id: currentSession.id,
            data: {
              tasks: updatedTasks.map((t) => ({
                id: t.id,
                title: t.title || t.text || 'Untitled Task',
                status: t.status || (t.completed ? 'done' : 'todo'),
                completed: t.completed,
                createdAt: t.createdAt as string,
                created_by: t.created_by || 'system',
                is_public: t.is_public || false
              })),
            },
          }),
        ).unwrap();
      } catch (error) {
        console.error('Failed to update task status:', error);
      }
    }
  };

  const editTask = (taskId: string) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    setEditingTask(task);
    setShowEditTaskModal(true);
  };

  const handleSaveEditedTask = async (newText: string, newDescription?: string, newStatus?: 'todo' | 'inprogress' | 'done') => {
    if (!editingTask || !newText.trim()) return;

    // Check if anything actually changed
    const textChanged = newText !== (editingTask.title || editingTask.text);
    const descChanged = newDescription !== editingTask.description;
    const statusChanged = newStatus !== undefined && newStatus !== (editingTask.status || (editingTask.completed ? 'done' : 'todo'));

    if (!textChanged && !descChanged && !statusChanged) return;

    const taskId = editingTask.id;
    const isCompleted = newStatus === 'done' || (newStatus === undefined && editingTask.completed);

    const updatedTasks = tasks.map((t) =>
      t.id === taskId ? {
        ...t,
        text: newText.trim(),
        title: newText.trim(),
        description: newDescription?.trim() || undefined,
        status: newStatus || t.status || (t.completed ? 'done' : 'todo'),
        completed: isCompleted
      } : t,
    );
    setTasks(updatedTasks);

    // Update session via API
    if (currentSession) {
      try {
        await dispatch(
          updateFocusSession({
            id: currentSession.id,
            data: {
              tasks: updatedTasks.map((t) => ({
                id: t.id,
                title: t.title || t.text || 'Untitled Task',
                description: t.description,
                status: t.status || (t.completed ? 'done' : 'todo'),
                completed: t.completed,
                createdAt: t.createdAt as string,
                created_by: t.created_by || 'system',
                assigned_to: t.assigned_to,
                is_public: t.is_public || false
              })),
            },
          }),
        ).unwrap();
      } catch (error) {
        console.error('Failed to update task:', error);
      }
    }

    // Reset editing state is handled by onClose in the modal or we can do it here if we want to be explicit
    // But modal onClose usually just hides it. We should probably clear editingTask when modal closes.
  };

  const deleteTask = async (taskId: string) => {
    const updatedTasks = tasks.filter((task) => task.id !== taskId);
    setTasks(updatedTasks);

    // Update session via API
    if (currentSession) {
      try {
        await dispatch(
          updateFocusSession({
            id: currentSession.id,
            data: {
              tasks: updatedTasks.map((t) => ({
                id: t.id,
                title: t.title || t.text || 'Untitled Task',
                status: t.status || (t.completed ? 'done' : 'todo'),
                completed: t.completed,
                createdAt: t.createdAt as string,
                created_by: t.created_by || 'system',
                is_public: t.is_public || false
              })),
            },
          }),
        ).unwrap();
      } catch (error) {
        console.error('Failed to delete task:', error);
      }
    }
  };

  const addNote = async () => {
    if (newNote.trim()) {
      const noteText = newNote.trim();
      const note = {
        id: Date.now().toString(),
        text: noteText,
        createdAt: new Date(),
      };
      const updatedNotes = [...notes, note];
      setNotes(updatedNotes);

      // Update sessionNotes array
      const updatedSessionNotes = [...sessionNotes, noteText];
      setSessionNotes(updatedSessionNotes);

      // Update session via API
      if (currentSession) {
        try {
          await dispatch(
            updateFocusSession({
              id: currentSession.id,
              data: {
                notes: updatedSessionNotes,
              },
            }),
          ).unwrap();
        } catch (error) {
          console.error('Failed to update notes:', error);
        }
      }

      // Check if sharing to team
      const shareCheckbox = document.getElementById(
        'shareNote',
      ) as HTMLInputElement;
      if (shareCheckbox && shareCheckbox.checked) {
        addSharedNote(noteText, true);
      }

      setNewNote('');

      // Reset textarea height
      const textarea = document.querySelector(
        'textarea[placeholder*="Add a new note"]',
      ) as HTMLTextAreaElement;
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = '60px';
      }
    }
  };

  const editNote = (noteId: string) => {
    const noteToEdit = notes.find((note) => note.id === noteId);
    if (!noteToEdit) return;
    setEditingNote(noteToEdit);
    setShowEditNoteModal(true);
  };

  const handleSaveEditedNote = async (newText: string) => {
    if (!editingNote || !newText.trim() || newText === editingNote.text) return;

    const noteId = editingNote.id;
    const updatedText = newText.trim();

    // Update note in local state
    const updatedNotes = notes.map((note) =>
      note.id === noteId ? { ...note, text: updatedText } : note,
    );
    setNotes(updatedNotes);

    // Update sessionNotes array by replacing the old note text with the new one
    const updatedSessionNotes = sessionNotes.map((noteText) =>
      noteText === editingNote.text ? updatedText : noteText,
    );
    setSessionNotes(updatedSessionNotes);

    // Update session via API
    if (currentSession) {
      try {
        await dispatch(
          updateFocusSession({
            id: currentSession.id,
            data: {
              notes: updatedSessionNotes,
            },
          }),
        ).unwrap();
      } catch (error) {
        console.error('Failed to update note:', error);
      }
    }
  };

  const deleteNote = async (noteId: string) => {
    const noteToDelete = notes.find((note) => note.id === noteId);
    if (!noteToDelete) return;

    // Remove note from local state
    const updatedNotes = notes.filter((note) => note.id !== noteId);
    setNotes(updatedNotes);

    // Update sessionNotes array by removing the deleted note text
    const updatedSessionNotes = sessionNotes.filter(
      (noteText) => noteText !== noteToDelete.text,
    );
    setSessionNotes(updatedSessionNotes);

    // Update session via API
    if (currentSession) {
      try {
        await dispatch(
          updateFocusSession({
            id: currentSession.id,
            data: {
              notes: updatedSessionNotes.length > 0 ? updatedSessionNotes : [],
            },
          }),
        ).unwrap();
      } catch (error) {
        console.error('Failed to update notes:', error);
      }
    }
  };

  const openNoteModal = (note: {
    id: string;
    text: string;
    createdAt: Date;
  }) => {
    setSelectedNote(note);
    setShowNoteModal(true);
  };

  // Team Mode Functions
  const toggleMute = () => {
    setIsMuted(!isMuted);
    setTeamMembers((prev) =>
      prev.map((member) =>
        member.id === '4' ? { ...member, isMuted: !isMuted } : member,
      ),
    );
  };

  const toggleScreenShare = async () => {
    if (isScreenSharing) {
      screenShareStreamRef.current?.getTracks().forEach((track) => track.stop());
      screenShareStreamRef.current = null;
      setIsScreenSharing(false);
    } else {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: false,
        });
        screenShareStreamRef.current = stream;
        stream.getVideoTracks()[0].onended = () => {
          screenShareStreamRef.current = null;
          setIsScreenSharing(false);
        };
        setIsScreenSharing(true);
        toast.success('Screen sharing started');
      } catch (err) {
        console.error('Screen share error:', err);
        toast.error('Could not start screen share. Please check permissions.');
      }
    }
  };

  const addSharedTask = (
    taskText: string,
    shareToTeam: boolean,
    assignedTo?: { id: string; name: string; avatar: string },
  ) => {
    if (shareToTeam) {
      const sharedTask = {
        id: Date.now().toString(),
        text: taskText,
        completed: false,
        createdBy: 'You',
        createdAt: new Date(),
        assignedTo,
      };
      setSharedTasks((prev) => [...prev, sharedTask]);
    }
  };

  const addSharedNote = (noteText: string, shareToTeam: boolean) => {
    if (shareToTeam) {
      const sharedNote = {
        id: Date.now().toString(),
        text: noteText,
        createdBy: 'You',
        createdAt: new Date(),
      };
      setSharedNotes((prev) => [...prev, sharedNote]);
    }
  };

  const toggleSharedTask = (taskId: string) => {
    setSharedTasks((prev) =>
      prev.map((task) =>
        task.id === taskId ? { ...task, completed: !task.completed } : task,
      ),
    );
  };

  const deleteSharedTask = (taskId: string) => {
    setSharedTasks((prev) => prev.filter((task) => task.id !== taskId));
  };

  const deleteSharedNote = (noteId: string) => {
    setSharedNotes((prev) => prev.filter((note) => note.id !== noteId));
  };



  const createRoom = () => {
    if (!sessionData.roomName.trim()) {
      alert('Please enter a room name');
      return;
    }

    if (!sessionData.projectId) {
      setShowProjectRequiredAlert(true);
      return;
    }

    const room = {
      id: Date.now().toString(),
      name: sessionData.roomName,
      roomId: sessionData.roomCode,
      code: sessionData.roomCode, // Added to fix TS error
      host: currentUser?.name || 'You',
      members: [
        {
          id: '4',
          name: currentUser?.name || 'You',
          avatar: 'U',
          isOnline: true,
          isMuted: false,
        },
      ],
      settings: {
        duration: sessionData.duration,
        breakReminders: sessionData.breakReminders,
        privacy: roomPrivacy,
        capacity: roomCapacity,
      },
      status: 'waiting' as const,
      createdAt: new Date(),
    };

    setCurrentRoom(room);
    setIsHost(true);
    startFocusSession();
  };

  const joinRoom = (code?: string) => {
    const effectiveCode = (code ?? roomCode).trim().toUpperCase();
    if (!effectiveCode) {
      alert('Please enter a room code or paste an invite link');
      return;
    }
    setRoomCode(effectiveCode);

    // Simulate finding a room by code
    const mockRoom = {
      id: 'room123',
      name: 'Focus Session Room',
      code: effectiveCode,
      host: 'John Doe',
      members: [
        { id: '1', name: 'John Doe', avatar: 'JD', isOnline: true, isMuted: false },
        { id: '2', name: 'Sarah Miller', avatar: 'SM', isOnline: true, isMuted: false },
        { id: '3', name: 'Alex Lee', avatar: 'AL', isOnline: true, isMuted: false },
      ],
      settings: {
        duration: 45,
        breakReminders: true,
        privacy: 'private' as const,
        capacity: 10,
      },
      status: 'waiting' as const,
      createdAt: new Date(),
    };

    setCurrentRoom(mockRoom);
    setIsHost(false);
    setSessionData((prev) => ({
      ...prev,
      goal: mockRoom.name,
      duration: mockRoom.settings.duration,
      breakReminders: mockRoom.settings.breakReminders,
      projectId: prev.projectId || projects?.[0]?.id,
      roomCode: effectiveCode,
    }));
    setPendingTeamJoinStart(true);
  };

  // Beep sound function for end warning
  const playBeepSound = () => {
    try {
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextClass();

      // Resume audio context if suspended (required by some browsers)
      if (audioContext.state === 'suspended') {
        audioContext
          .resume()
          .then(() => {
            playBeepSequence(audioContext);
          })
          .catch((err) => {
            console.error('Failed to resume audio context:', err);
            playBeepSequence(audioContext);
          });
      } else {
        playBeepSequence(audioContext);
      }
    } catch (error) {
      console.error('Error creating audio context:', error);
    }
  };

  // Helper function to play beep sequence
  const playBeepSequence = (audioContext: AudioContext) => {
    // Play three beeps for better noticeability
    const playBeep = (delay: number) => {
      setTimeout(() => {
        try {
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();

          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);

          oscillator.frequency.value = 800; // Pleasant beep frequency
          oscillator.type = 'sine';

          // Make it louder and longer
          gainNode.gain.setValueAtTime(0.6, audioContext.currentTime);
          gainNode.gain.exponentialRampToValueAtTime(
            0.01,
            audioContext.currentTime + 0.4,
          );

          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.4);
        } catch (err) {
          console.error('Error playing beep:', err);
        }
      }, delay);
    };

    // Play three beeps with slight delay between them
    playBeep(0);
    playBeep(300);
    playBeep(600);
  };

  const formatTime = (seconds: number) => {
    // Round to nearest integer to avoid decimal seconds
    const totalSeconds = Math.round(seconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    // Always show minutes and seconds with 2 digits: MM:SS
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleResumeContinue = () => {
    // User chose to "Resume": just close modal and unpause
    // The timer was missed, but we ignore it, effectively extending the session
    setShowResumeModal(false);
    setGapDetectionTime(null);
    resumeSession();
  };

  const handleResumeSync = () => {
    // User chose to "Sync": subtract missed time
    // Calculate total missed time including the time modal was open
    const additionalMissed = gapDetectionTime
      ? (Date.now() - gapDetectionTime) / 1000
      : 0;
    const totalMissed = missedSessionTime + additionalMissed;

    const newTime = sessionTimeLeft - totalMissed;

    setShowResumeModal(false);
    setGapDetectionTime(null);

    if (newTime <= 0) {
      // Session finished during the gap
      finishSession();
    } else {
      setSessionTimeLeft(newTime);
      resumeSession();
    }
  };

  return (
    <div className='min-h-screen bg-[#FFF8F0] relative overflow-hidden'>
      {showFeedbackBanner && (
        <div
          className={`fixed top-[57px] left-0 right-0 z-30 bg-orange-50/95 backdrop-blur-sm border-b border-orange-200/50 px-4 py-2.5 shadow-sm transition-all duration-500 ease-in-out transform ${isBannerVisible
            ? 'translate-y-0 opacity-100'
            : '-translate-y-4 opacity-0 pointer-events-none'
            }`}
        >
          <div className='max-w-7xl mx-auto relative flex items-center justify-center'>
            <div className='flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 pr-8'>
              <p className='text-gray-900 text-xs sm:text-sm font-semibold text-center sm:text-left'>
                Help shape Focuzon's roadmap! Share your feedback and help us
                improve.
              </p>
              <button
                onClick={() => {
                  navigate('/');
                  setTimeout(() => {
                    const contactSection = document.getElementById('contact');
                    if (contactSection) {
                      contactSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }, 100);
                }}
                className='bg-white text-orange-600 border border-orange-300 px-3 py-1 sm:px-4 sm:py-1.5 rounded-md text-xs sm:text-sm font-medium hover:bg-orange-50 hover:border-orange-400 transition-all whitespace-nowrap shadow-sm'
              >
                Give Feedback
              </button>
            </div>
            <button
              onClick={handleCloseBanner}
              className='absolute right-0 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 hover:bg-orange-100/50 rounded-full transition-colors focus:outline-none focus:ring-0'
              aria-label='Close feedback banner'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-5 w-5'
                viewBox='0 0 20 20'
                fill='currentColor'
              >
                <path
                  fillRule='evenodd'
                  d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                  clipRule='evenodd'
                />
              </svg>
            </button>
          </div>
        </div>
      )}
      <ResumeSessionModal
        isOpen={showResumeModal}
        missedTimeSeconds={missedSessionTime}
        gapDetectionTime={gapDetectionTime}
        onContinue={handleResumeContinue}
        onSync={handleResumeSync}
      />

      {/* Focus Mode Tabs */}
      <div
        className={`max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 pb-4 sm:pb-6 md:pb-8 relative z-10 animate-slide-up-fade transition-all duration-500 ease-in-out ${isBannerVisible ? 'pt-24 sm:pt-8' : 'pt-6'
          }`}
      >
        {/* Mode Selection Tabs - Solo vs Team */}
        <div className="flex justify-center mb-5 sm:mb-6">
          <div className="bg-white/60 rounded-full p-1 border border-orange-100/60 inline-flex">
            <button
              onClick={() => setActiveMode('solo')}
              className={`px-5 sm:px-7 py-2 sm:py-2.5 rounded-full font-medium transition-all duration-200 text-sm sm:text-base ${activeMode === 'solo'
                ? 'bg-orange-500 text-white'
                : 'text-gray-500 hover:text-gray-700 hover:bg-orange-50/30'
                }`}
            >
              Solo Mode
            </button>
            <button
              onClick={() => setActiveMode('team')}
              className={`px-5 sm:px-7 py-2 sm:py-2.5 rounded-full font-medium transition-all duration-200 text-sm sm:text-base ${activeMode === 'team'
                ? 'bg-orange-500 text-white'
                : 'text-gray-500 hover:text-gray-700 hover:bg-orange-50/30'
                }`}
            >
              Team Mode
            </button>
          </div>
        </div>

        {/* Solo Mode Content */}
        {activeMode === 'solo' && (
          <SoloModeContent
            key={formResetKey}
            sessionData={sessionData}
            setSessionData={setSessionData}
            onStartSession={startFocusSession}
            onManageProjects={() => {
              setProjectModalView('list');
              setShowProjectManagementModal(true);
            }}
            onCreateProjects={() => {
              setProjectModalView('create');
              setShowProjectManagementModal(true);
            }}
            projectSelectorRef={projectSelectorRef}
          />
        )}

        {/* Team Mode Content */}
        {activeMode === 'team' && (
          <TeamModeContent
            sessionData={sessionData}
            setSessionData={setSessionData}
            teamTab={teamTab}
            setTeamTab={setTeamTab}
            roomCode={roomCode}
            setRoomCode={setRoomCode}
            onCreateRoom={createRoom}
            onJoinRoom={joinRoom}
            onManageProjects={() => {
              setProjectModalView('list');
              setShowProjectManagementModal(true);
            }}
            onCreateProjects={() => {
              setProjectModalView('create');
              setShowProjectManagementModal(true);
            }}
            projectSelectorRef={projectSelectorRef}
          />
        )}

        <CustomMusicModal
          isOpen={showCustomMusicModal}
          onClose={() => setShowCustomMusicModal(false)}
          onSave={(url) => {
            // Update session data
            setSessionData((prev) => ({
              ...prev,
              music: 'custom' as FocusMusicType,
              customMusicURL: url,
            }));

            // Update API if session exists
            if (currentSession) {
              dispatch(
                updateFocusSession({
                  id: currentSession.id,
                  data: {
                    music: 'custom' as FocusMusicType,
                    customMusicURL: url,
                  },
                }),
              ).catch(console.error);
            }

            // Manually stop other audio and play custom URL
            // This replicates logic from handleChangeMusic but forcefully playing the custom URL we just got
            if (noiseAudioRef.current) {
              noiseAudioRef.current.pause();
              noiseAudioRef.current.currentTime = 0;
            }
            if (natureAudioRef.current) {
              natureAudioRef.current.pause();
              natureAudioRef.current.currentTime = 0;
            }
            if (binauralAudioRef.current) {
              binauralAudioRef.current.pause();
              binauralAudioRef.current.currentTime = 0;
            }
            if (youtubePlayerRef.current) {
              try {
                youtubePlayerRef.current.pauseVideo();
              } catch (e) { }
            }
            setIsMusicPlaying(false);

            // Play the new custom URL
            const videoId = extractYouTubeVideoId(url);
            if (
              videoId &&
              youtubePlayerRef.current &&
              youtubePlayerRef.current.loadVideoById
            ) {
              // Wait a tick for safety or just call immediately
              try {
                youtubePlayerRef.current.loadVideoById(videoId);
                youtubePlayerRef.current.setVolume(musicVolume);
                youtubePlayerRef.current.playVideo();
                setIsMusicPlaying(true);
              } catch (err) {
                console.error('Error playing custom music after save:', err);
              }
            }
          }}
          currentUrl={
            sessionData.customMusicURL || currentSession?.customMusicURL
          }
        />

        <RecentSessions
          sessions={recentSessions}
          expandedSessions={expandedSessions}
          setExpandedSessions={setExpandedSessions}
          onViewAll={() => setShowAllSessions(true)}
          onViewDetails={handleViewSessionDetails}
          onRepeatSession={handleRepeatSession}
        />

        <FocusAnalytics />

        <GamesSection
          userBadges={userBadges}
          availableBadges={availableBadges}
        />
      </div>

      <AllSessionsModal
        open={showAllSessions}
        onClose={() => setShowAllSessions(false)}
        allSessions={sessions.map((session) => ({
          id: session.id,
          type: session.type,
          goal: session.goal,
          duration: session.duration,
          completedAt: session.completedAt || session.updatedAt,
          notes: Array.isArray(session.notes)
            ? session.notes.join(' ')
            : session.notes || '',
          members: session.members || [],
          xp: session.xp || 0,
          music: session.music,
          tasks: session.tasks || [],
        }))}
        filteredSessions={filteredSessions.map((session) => ({
          id: session.id,
          type: session.type,
          goal: session.goal,
          duration: session.duration,
          completedAt: session.completedAt || session.updatedAt,
          notes: Array.isArray(session.notes)
            ? session.notes.join(' ')
            : session.notes || '',
          members: session.members || [],
          xp: session.xp || 0,
          music: session.music,
          tasks: session.tasks || [],
        }))}
        sessionSearch={sessionSearch}
        setSessionSearch={setSessionSearch}
        sessionFilter={sessionFilter}
        setSessionFilter={setSessionFilter}
        expandedSessions={expandedSessions}
        setExpandedSessions={setExpandedSessions}
        onViewDetails={handleViewSessionDetails}
        onRepeatSession={handleRepeatSession}
      />

      {/* Focus Session Page */}
      <FocusSessionModal
        open={showFocusSession}
        activeMode={activeMode}
        sessionData={sessionData}
        currentSession={currentSession}
        sessionTimeLeft={sessionTimeLeft}
        sessionStarted={sessionStarted}
        sessionPaused={sessionPaused}
        sessionCompleted={sessionCompleted}
        sessionTab={sessionTab}
        setSessionTab={setSessionTab}
        showTasksTooltip={showTasksTooltip}
        setShowTasksTooltip={setShowTasksTooltip}
        showEndWarningMessage={showEndWarningMessage}
        beepSoundEnabled={beepSoundEnabled}
        setBeepSoundEnabled={setBeepSoundEnabled}
        beepWarningTime={beepWarningTime}
        breakRemindersEnabled={breakRemindersEnabled}
        setBreakRemindersEnabled={setBreakRemindersEnabled}
        notes={notes}
        newNote={newNote}
        setNewNote={setNewNote}
        tasks={tasks}
        newTask={newTask}
        setNewTask={setNewTask}
        reminders={reminders}
        setReminders={setReminders}
        teamMembers={teamMembers}
        sharedTasks={sharedTasks}
        sharedNotes={sharedNotes}
        isMuted={isMuted}
        isScreenSharing={isScreenSharing}
        musicVolume={musicVolume}
        isMusicPlaying={isMusicPlaying}
        isMusicMuted={isMusicMuted}
        formatTime={formatTime}
        pipWindow={pipWindow}
        requestPiP={requestPiP}
        closePiP={closePiP}
        isPiPSupported={isPiPSupported}
        checkAutoPiPPermission={checkAutoPiPPermission}
        onClose={() => {
          // Hide tooltip when modal closes
          setShowTasksTooltip(false);
          // If session is completed, just close the modal
          if (sessionCompleted) {
            setShowFocusSession(false);
            screenShareStreamRef.current?.getTracks().forEach((t) => t.stop());
            screenShareStreamRef.current = null;
            setIsScreenSharing(false);
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            // Clear notes and input data when modal is closed
            setSessionNotes([]);
            setNotes([]);
            setTasks([]);
            setNewNote('');
            setReminders([]);
            clearSessionData();
          } else {
            // Ask for confirmation before closing active session
            endSession();
          }
        }}
        onConfirmPreSession={confirmPreSession}
        onPauseSession={pauseSession}
        onResumeSession={resumeSession}
        onEndSession={endSession}
        onConfirmEndSession={confirmEndSession}
        onExtendSession={extendSession}
        onAddNote={addNote}
        onEditNote={editNote}
        onDeleteNote={deleteNote}
        onOpenNoteModal={openNoteModal}
        onAddTask={addTask}
        onToggleTask={toggleTask}
        onEditTask={editTask}
        onDeleteTask={deleteTask}
        onToggleMute={toggleMute}
        onToggleScreenShare={toggleScreenShare}
        screenShareStreamRef={screenShareStreamRef}
        onToggleSharedTask={toggleSharedTask}
        onDeleteSharedTask={deleteSharedTask}
        onSetSelectedNote={setSelectedNote}
        onSetShowNoteModal={setShowNoteModal}
        onSetShowMembersPopup={setShowMembersPopup}
        onSetMusicVolume={setMusicVolume}
        onSetIsMusicPlaying={setIsMusicPlaying}
        onSetIsMusicMuted={setIsMusicMuted}
        onChangeMusic={handleChangeMusic}
        noiseAudioRef={noiseAudioRef}
        natureAudioRef={natureAudioRef}
        binauralAudioRef={binauralAudioRef}
        youtubePlayerRef={youtubePlayerRef}
        missedBreakCount={missedBreakCount}
      />

      {/* Edit Task Modal */}
      <EditTaskModal
        open={showEditTaskModal}
        roomId={currentRoom?.id}
        taskId={editingTask?.id}
        initialText={editingTask?.title || editingTask?.text || ''}
        initialDescription={editingTask?.description || ''}
        initialStatus={editingTask?.status || (editingTask?.completed ? 'done' : 'todo')}
        onClose={() => {
          setShowEditTaskModal(false);
          setEditingTask(null);
        }}
        onSave={handleSaveEditedTask}
      />

      {/* Edit Note Modal */}
      <EditNoteModal
        open={showEditNoteModal}
        initialText={editingNote?.text || ''}
        onClose={() => {
          setShowEditNoteModal(false);
          setEditingNote(null);
        }}
        onSave={handleSaveEditedNote}
      />

      {/* Pre-Session Modal */}
      <PreSessionModal
        open={showPreSessionModal}
        onClose={() => setShowPreSessionModal(false)}
        onConfirm={confirmPreSession}
        sessionData={sessionData}
        setSessionData={setSessionData}
      />

      {/* Project Required Alert Modal */}
      <AlertModal
        open={showProjectRequiredAlert}
        onClose={() => setShowProjectRequiredAlert(false)}
        title='Project Selection Required'
        message='Please select a project before starting your focus session. This helps organize and track your work effectively.'
        type='warning'
      />

      {/* Project Management Modal */}
      <ProjectManagementModal
        isOpen={showProjectManagementModal}
        onClose={() => setShowProjectManagementModal(false)}
        initialView={projectModalView}
        mode={projectModalView === 'create' ? 'create' : 'manage'}
        onProjectCreated={(projectId) => {
          setSessionData((prev) => ({ ...prev, projectId }));
          dispatch(setSelectedProject(projectId));
        }}
      />

      {/* End Session Confirmation Modal */}
      {showEndSessionModal && !sessionCompleted && (
        <div className='fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4'>
          <div className='bg-[#FFF8F0] rounded-2xl p-6 sm:p-8 max-w-md w-full border border-gray-200/60 shadow-xl'>
            <div className='text-center space-y-6'>
              {/* Icon */}
              <div className='flex justify-center'>
                <div className='w-16 h-16 sm:w-20 sm:h-20 bg-orange-50 rounded-full flex items-center justify-center border border-orange-200/50'>
                  <svg
                    className='w-8 h-8 sm:w-10 sm:h-10 text-orange-500'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                    />
                  </svg>
                </div>
              </div>

              {/* Title */}
              <div className='space-y-2'>
                <h3 className='text-2xl sm:text-3xl font-medium text-gray-900'>
                  End Focus Session?
                </h3>
                <p className='text-sm sm:text-base text-gray-600 font-light leading-relaxed'>
                  Are you sure you want to end this session? Your progress will
                  be saved.
                </p>
              </div>

              {/* Action Buttons */}
              <div className='flex flex-col sm:flex-row gap-3 pt-2'>
                <button
                  onClick={() => setShowEndSessionModal(false)}
                  className='flex-1 px-6 py-3 bg-white border border-gray-200/60 hover:border-gray-300/60 text-gray-700 rounded-lg font-normal transition-colors text-sm'
                >
                  Cancel
                </button>
                <button
                  onClick={confirmEndSession}
                  className='flex-1 px-6 py-3 bg-white border border-orange-600 hover:bg-orange-50 text-orange-600 rounded-lg font-medium transition-colors text-sm'
                >
                  End Session
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Note View Modal */}
      {showNoteModal && selectedNote && (
        <div className='fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4'>
          <div className='bg-[#FFF8F0] rounded-2xl p-8 max-w-2xl w-full border border-gray-200/60 shadow-xl'>
            <div className='flex justify-between items-center mb-6'>
              <h3 className='text-2xl font-bold text-gray-900'>Note Details</h3>
              <button
                onClick={() => setShowNoteModal(false)}
                className='text-gray-400 hover:text-gray-700 text-2xl font-bold p-1 hover:bg-orange-50 rounded-lg transition-colors'
              >
                ×
              </button>
            </div>

            <div className='space-y-4'>
              <div className='bg-white rounded-xl p-4 border border-gray-200/60 max-h-[60vh] overflow-y-auto'>
                <div className='text-gray-500 text-sm mb-2'>
                  Created: {selectedNote.createdAt.toLocaleString()}
                </div>
                <div className='text-gray-900 text-lg leading-relaxed whitespace-pre-wrap'>
                  {selectedNote.text}
                </div>
              </div>

              <div className='flex justify-end space-x-3'>
                <button
                  onClick={() => setShowNoteModal(false)}
                  className='px-6 py-3 bg-white border border-gray-200/60 hover:bg-orange-50 text-gray-700 rounded-xl font-medium transition-colors'
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    deleteNote(selectedNote.id);
                    setShowNoteModal(false);
                  }}
                  className='px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-colors'
                >
                  Delete Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Members Popup Modal */}
      {showMembersPopup && (
        <div className='fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4'>
          <div className='bg-[#FFF8F0] rounded-2xl p-6 max-w-md w-full border border-gray-200/60 shadow-xl'>
            <div className='flex justify-between items-center mb-6'>
              <h3 className='text-2xl font-bold text-gray-900'>Team Members</h3>
              <button
                onClick={() => setShowMembersPopup(false)}
                className='text-gray-400 hover:text-gray-700 text-2xl font-bold p-1 hover:bg-orange-50 rounded-lg transition-colors'
              >
                ×
              </button>
            </div>

            <div className='max-h-80 overflow-y-auto space-y-3'>
              {teamMembers.map((member) => (
                <div
                  key={member.id}
                  className='flex items-center space-x-4 p-4 bg-white rounded-xl border border-gray-200/60 hover:border-orange-200/60 transition-colors'
                >
                  <div className='relative'>
                    <div className='w-12 h-12 bg-orange-100 border border-orange-200/60 rounded-full flex items-center justify-center text-orange-600 font-bold text-lg'>
                      {member.avatar}
                    </div>
                    <div
                      className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${member.isOnline ? 'bg-green-500' : 'bg-gray-400'}`}
                    ></div>
                    {member.isMuted && (
                      <div className='absolute -top-1 -right-1 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center'>
                        <span className='text-white text-xs'>🔇</span>
                      </div>
                    )}
                  </div>
                  <div className='flex-1'>
                    <div className='text-gray-900 font-semibold'>
                      {member.name}
                    </div>
                    <div className='text-gray-500 text-sm'>
                      {member.isOnline ? 'Online' : 'Offline'} •{' '}
                      {member.isMuted ? 'Muted' : 'Unmuted'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Micro Reminder (Non-intrusive) */}
      {showMicroReminder && (
        <div className='fixed top-4 right-4 z-50 animate-in slide-in-from-top-5 duration-300'>
          <div className='bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg border border-orange-200/50 flex items-center gap-3 max-w-sm'>
            <div className='flex-shrink-0 text-2xl'>👀</div>
            <div className='flex-1'>
              <p className='text-sm font-medium text-gray-900'>
                Blink & look away for 10 seconds
              </p>
            </div>
            <button
              onClick={() => setShowMicroReminder(false)}
              className='flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors'
            >
              <svg
                className='w-4 h-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Break Reminder Modal */}
      {showBreakReminder && currentBreakReminder && (
        <div className='fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4'>
          <div className='bg-[#FFF8F0] rounded-3xl p-6 sm:p-8 max-w-lg w-full border border-orange-200/50 shadow-2xl relative overflow-hidden'>
            {/* Decorative background gradient */}
            <div className='absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-orange-100/30 to-orange-50/20 rounded-full blur-3xl -z-0'></div>
            <div className='absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-orange-100/20 to-transparent rounded-full blur-2xl -z-0'></div>

            <div className='relative z-10'>
              {!breakStarted ? (
                // Initial prompt view
                <>
                  {/* Auto-close Timer */}
                  <div className='text-center mb-4'>
                    <p className='text-xs text-orange-600/70 font-medium bg-orange-50 inline-block px-3 py-1 rounded-full border border-orange-100'>
                      Auto-closing in {breakWarningTimer} seconds
                    </p>
                  </div>
                  <div className='text-center mb-8'>
                    {/* Large animated icon */}
                    <div className='flex justify-center mb-6'>
                      <div className='relative'>
                        <div className='absolute inset-0 bg-gradient-to-br from-orange-400/20 to-orange-300/10 rounded-full blur-xl animate-pulse'></div>
                        <div className='relative bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-3xl p-6 border border-orange-200/50 shadow-lg'>
                          <span className='text-7xl block'>
                            {currentBreakReminder.icon}
                          </span>
                        </div>
                      </div>
                    </div>

                    <h3 className='text-3xl sm:text-4xl font-bold text-gray-900 mb-3 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent'>
                      {currentBreakReminder.title}
                    </h3>
                    <p className='text-gray-600 mb-6 text-base sm:text-lg leading-relaxed max-w-md mx-auto'>
                      {currentBreakReminder.description}
                    </p>

                    {/* Enhanced category and duration */}
                    <div className='flex items-center justify-center gap-4 mb-2'>
                      <span className='px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full text-xs font-bold uppercase tracking-wider shadow-md'>
                        {currentBreakReminder.category}
                      </span>
                      <div className='flex items-center gap-2 text-gray-500'>
                        <svg
                          className='w-4 h-4'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                          />
                        </svg>
                        <span className='text-sm font-medium'>
                          {currentBreakReminder.duration}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className='flex space-x-3'>
                    <button
                      onClick={closeBreakReminder}
                      className='flex-1 px-6 py-3.5 bg-white border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl font-semibold transition-all duration-300 shadow-sm hover:shadow-md'
                    >
                      Do it later
                    </button>
                    <button
                      onClick={startBreak}
                      className='flex-1 px-6 py-3.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl font-bold transition-all duration-300 shadow-lg shadow-orange-300/50 hover:shadow-xl hover:shadow-orange-400/60 transform hover:scale-[1.02]'
                    >
                      Start Break
                    </button>
                  </div>
                </>
              ) : (
                // Break instructions view
                <>
                  <div className='mb-6'>
                    {/* Enhanced header with Timer */}
                    <div className='flex items-start gap-4 mb-6 pb-5 border-b border-orange-100'>
                      <div className='relative flex-shrink-0'>
                        <div className='absolute inset-0 bg-gradient-to-br from-orange-400/20 to-orange-300/10 rounded-2xl blur-lg'></div>
                        <div className='relative bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-2xl p-4 border border-orange-200/50'>
                          <span className='text-5xl block'>
                            {currentBreakReminder.icon}
                          </span>
                        </div>
                      </div>
                      <div className='flex-1 pt-1'>
                        <div className='flex justify-between items-start'>
                          <h3 className='text-2xl sm:text-3xl font-bold text-gray-900 mb-2'>
                            {currentBreakReminder.title
                              .replace(/^[\u{1F300}-\u{1F9FF}]+\s*/u, '')
                              .trim()}
                          </h3>
                          {breakStarted && (
                            <div className='text-2xl font-mono text-orange-600 font-bold bg-orange-50 px-3 py-1 rounded-lg border border-orange-200'>
                              {Math.floor(breakTimeLeft / 60)}:
                              {(breakTimeLeft % 60).toString().padStart(2, '0')}
                            </div>
                          )}
                        </div>
                        <p className='text-base text-gray-600 leading-relaxed'>
                          {currentBreakReminder.description}
                        </p>
                      </div>
                    </div>

                    {/* Enhanced category and duration */}
                    <div className='flex items-center justify-between mb-6 pb-4 border-b border-orange-100'>
                      <span className='px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full text-xs font-bold uppercase tracking-wider shadow-md'>
                        {currentBreakReminder.category}
                      </span>
                      <div className='flex items-center gap-4'>
                        <div className='flex items-center gap-2 text-gray-600'>
                          <svg
                            className='w-4 h-4'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                            />
                          </svg>
                          <span className='text-sm font-semibold'>
                            {currentBreakReminder.duration}
                          </span>
                        </div>
                        {/* Voice Toggle */}
                        <button
                          onClick={toggleVoice}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-medium text-xs transition-all duration-300 ${voiceEnabled
                            ? 'bg-orange-500 text-white shadow-md hover:bg-orange-600'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          title={
                            voiceEnabled
                              ? 'Disable voice guidance'
                              : 'Enable voice guidance'
                          }
                        >
                          {isSpeaking ? (
                            <svg
                              className='w-4 h-4 animate-pulse'
                              fill='currentColor'
                              viewBox='0 0 24 24'
                            >
                              <path d='M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z' />
                              <path d='M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z' />
                            </svg>
                          ) : (
                            <svg
                              className='w-4 h-4'
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z'
                              />
                            </svg>
                          )}
                          <span className='hidden sm:inline'>
                            {voiceEnabled
                              ? isSpeaking
                                ? 'Speaking...'
                                : 'Voice On'
                              : 'Voice Off'}
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* Enhanced instructions */}
                    <div className='space-y-4'>
                      <h4 className='text-lg font-bold text-gray-900 mb-4 flex items-center gap-2'>
                        <span className='w-1 h-6 bg-gradient-to-b from-orange-500 to-orange-600 rounded-full'></span>
                        Instructions
                      </h4>
                      <ol className='space-y-4'>
                        {currentBreakReminder.instructions.map(
                          (instruction: string, index: number) => (
                            <li
                              key={index}
                              className='flex items-start gap-4 group'
                            >
                              <div className='relative flex-shrink-0'>
                                <div className='absolute inset-0 bg-orange-200/50 rounded-full blur-md group-hover:blur-lg transition-all'></div>
                                <span className='relative w-9 h-9 bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-lg group-hover:scale-110 transition-transform'>
                                  {index + 1}
                                </span>
                              </div>
                              <span className='text-gray-700 leading-relaxed text-base pt-1.5 group-hover:text-gray-900 transition-colors'>
                                {instruction}
                              </span>
                            </li>
                          ),
                        )}
                      </ol>
                    </div>
                  </div>

                  <div className='flex flex-col items-center gap-3 pt-4'>
                    {voiceEnabled && (
                      <div className='flex items-center gap-2 text-sm text-orange-600 bg-orange-50 px-4 py-2 rounded-lg border border-orange-200/50'>
                        <svg
                          className='w-4 h-4'
                          fill='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path d='M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z' />
                          <path d='M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z' />
                        </svg>
                        <span className='font-medium'>
                          {isSpeaking
                            ? 'Voice guidance is speaking...'
                            : 'Voice guidance is active'}
                        </span>
                      </div>
                    )}
                    <button
                      onClick={continueSessionFromBreak}
                      className='px-10 py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl font-bold text-base transition-all duration-300 shadow-lg shadow-orange-300/50 hover:shadow-xl hover:shadow-orange-400/60 transform hover:scale-[1.02]'
                    >
                      Continue Session
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Session Details Modal */}
      <SessionDetailsModal
        open={showSessionDetailsModal}
        onClose={() => {
          setShowSessionDetailsModal(false);
          setSelectedSessionForDetails(null);
        }}
        session={selectedSessionForDetails}
      />

      {/* Hidden audio elements for music tracks */}
      <audio
        ref={noiseAudioRef}
        src='/music/wave.mp3'
        preload='auto'
        style={{ display: 'none' }}
      />
      <audio
        ref={natureAudioRef}
        src='/music/nature.mp3'
        preload='auto'
        style={{ display: 'none' }}
      />
      <audio
        ref={binauralAudioRef}
        src='/music/binaural.mp3'
        preload='auto'
        style={{ display: 'none' }}
      />

      {/* Hidden YouTube iframe container for custom music */}
      <div
        ref={youtubePlayerContainerRef}
        style={{
          position: 'absolute',
          top: '-9999px',
          left: '-9999px',
          visibility: 'hidden',
        }}
      />

      <SessionCompletionModal
        open={showCompletionModal}
        onClose={() => {
          setShowCompletionModal(false);
          setSessionCompleted(false);
          setShowFocusSession(false);
          closePiP();
        }}
        onStartAnother={() => {
          setShowCompletionModal(false);
          setSessionCompleted(false);
          closePiP();
        }}
        duration={completedSessionDuration}
        goal={sessionData.goal || 'Focus Session'}
      />
    </div>
  );
};

export default Dashboard;
